import axios from "utils/httpClient";
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Button
} from "reactstrap";
import HotelIcon from "@material-ui/icons/Hotel";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import EditIcon from "@material-ui/icons/Edit";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BuildIcon from "@material-ui/icons/Build";

// Template generator function for 3D Building (Building 1 pink with cats, Building 2 light green without cats)
function generateBuildingHTML({
  buildingId = 1,
  buildingName = "อาคาร 1",
  theme = "pink",
  hasCats = (buildingId === 1),
  roomsData = [],
  staffCandidates = [],
  isTraineeMode = false
}) {
  const isSlate = theme === "gray" || theme === "slate" || buildingId === 4;
  const isCream = !isSlate && (theme === "cream" || theme === "orange" || buildingId === 3);
  const isGreen = !isSlate && !isCream && (theme === "green" || buildingId === 2);
  const showCats = Boolean(hasCats) && buildingId === 1;
  const showDogAndBike = Number(buildingId) === 4;

  // Map real database rooms into Floor 1, Floor 2, Floor 3
  const getRoomInfo = (roomNo) => {
    const found = roomsData.find(
      (r) =>
        String(r.RoomNumber || r.roomNumber) === String(roomNo) &&
        (Number(r.DormitoryID || r.dormitoryID) === Number(buildingId) || String(r.BuildingNo || r.buildingNo) === String(buildingId))
    );
    const rawResidents = found ? (found.CurrentResidents || found.currentResidents || []) : [];
    const capacity = found ? Number(found.BedCapacity || found.bedCapacity || 2) : 2;

    const isSpecialFamilyRoom = (buildingId === 4 && String(roomNo) === "424");
    const isDutyRoom = (buildingId === 4 && String(roomNo) === "411");
    const isFamilyRoom = isSpecialFamilyRoom || (found && (found.IsFamilyRoom || found.isFamilyRoom || found.RoomType === "ครอบครัว" || found.roomType === "ครอบครัว"));

    // Parse all occupants including family members and co-residents
    const allOccupants = [];
    rawResidents.forEach((res) => {
      const mName = res.TraineeName || res.traineeName;
      if (mName) {
        const cleanName = mName.replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
        const foundStaff = (staffCandidates || []).find((s) => {
          if (res.StaffCode && s.empCode && String(res.StaffCode) === String(s.empCode)) return true;
          if (res.EmployeeKey && s.id && String(res.EmployeeKey) === String(s.id)) return true;
          if (res.CitizenID && s.citizenId && String(res.CitizenID) === String(s.citizenId)) return true;
          const sName = (s.name || '').replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
          return cleanName && sName && (cleanName === sName || sName.includes(cleanName) || cleanName.includes(sName));
        });

        let avatarUrl = res.avatarUrl || res.AvatarUrl || null;
        let isStaffLinked = false;
        if (foundStaff) {
          isStaffLinked = true;
          const target = foundStaff.userName || foundStaff.empCode || foundStaff.id;
          avatarUrl = foundStaff.avatarUrl || (target ? `https://e.trangskin.go.th/avatar/${target}.jpg` : avatarUrl);
        } else if (res.StaffCode || res.EmployeeKey) {
          isStaffLinked = true;
          const target = res.StaffCode || res.EmployeeKey;
          if (!avatarUrl && target) avatarUrl = `https://e.trangskin.go.th/avatar/${target}.jpg`;
        } else if (res.TraineeType === "STAFF" || res.traineeType === "STAFF") {
          isStaffLinked = true;
        }

        let gender = buildingId === 1 ? 'F' : (buildingId === 4 ? 'M' : 'F');
        if (mName) {
          if (/^(นางสาว|นาง|พญ\.|น\.ส\.)/.test(mName)) gender = 'F';
          else if (/^(นาย|นพ\.)/.test(mName)) gender = 'M';
        }

        allOccupants.push({
          name: mName,
          gender: gender,
          role: res.PositionText || res.positionText || (foundStaff ? foundStaff.position : null) || (
            buildingId === 2 ? "แพทย์" :
            (buildingId === 3 ? "บุคลากร (ผู้พักหลัก)" :
            (buildingId === 4 ? (isSpecialFamilyRoom ? "บุคลากร / ครอบครัวพิเศษ" : "เจ้าหน้าที่ / นศ.ชาย") :
            "เจ้าหน้าที่ / นศ.หญิง"))
          ),
          isFamily: false,
          isStaffLinked: isStaffLinked,
          avatarUrl: avatarUrl
        });
      }

      const coRaw = res.CoResidents || res.coResidents || "";
      if (coRaw && typeof coRaw === "string") {
        const members = coRaw.split(/[,;\n]+/).map((m) => m.trim()).filter(Boolean);
        members.forEach((m) => {
          let famGender = buildingId === 1 ? 'F' : (buildingId === 4 ? 'M' : 'F');
          if (/^(นางสาว|นาง|พญ\.|น\.ส\.)/.test(m)) famGender = 'F';
          else if (/^(นาย|นพ\.)/.test(m)) famGender = 'M';
          allOccupants.push({
            name: m,
            gender: famGender,
            role: "สมาชิกครอบครัว / ผู้ติดตาม",
            isFamily: true,
            isStaffLinked: false,
            avatarUrl: null
          });
        });
      }
    });

    const occ = allOccupants.length;
    const isOccupied = occ > 0 || (found && found.Status === "OCCUPIED");
    const isMaintenance = found && (found.Status === "MAINTENANCE" || found.status === "MAINTENANCE");

    const tenants = allOccupants.map((occItem, i) => {
      const paletteHair = ["#1e293b", "#451a03", "#312e81", "#78350f", "#0f172a"];
      const paletteShirt = isSlate
        ? ["#334155", "#475569", "#64748b", "#0284c7", "#059669", "#7c3aed"]
        : (isCream
          ? ["#d97706", "#b45309", "#92400e", "#059669", "#2563eb", "#7c3aed"]
          : (isGreen
            ? ["#10b981", "#059669", "#0d9488", "#2563eb", "#8b5cf6", "#d97706"]
            : ["#ec4899", "#8b5cf6", "#06b6d4", "#f43f5e", "#10b981", "#3b82f6"]));
      return {
        name: occItem.name,
        gender: occItem.gender || (buildingId === 1 ? 'F' : (buildingId === 4 ? 'M' : 'F')),
        role: occItem.role,
        isFamily: occItem.isFamily,
        isStaffLinked: occItem.isStaffLinked,
        avatarUrl: occItem.avatarUrl,
        hair: paletteHair[i % paletteHair.length],
        shirt: paletteShirt[i % paletteShirt.length],
        skin: i % 2 === 0 ? "#fde68a" : "#fed7aa"
      };
    });

    const hasStaffResident = rawResidents.some((r) => 
      r.TraineeType === "STAFF" || 
      r.traineeType === "STAFF" || 
      r.StaffCode || 
      r.EmployeeKey || 
      r.CitizenID ||
      (r.PositionText && r.PositionText.includes("เจ้าหน้าที่")) ||
      (r.positionText && r.positionText.includes("เจ้าหน้าที่"))
    ) || allOccupants.some((occItem) => occItem.isStaffLinked);

    const hasStudentResident = rawResidents.some((r) =>
      r.TraineeType === "STUDENT" || 
      r.traineeType === "STUDENT" || 
      r.TraineeType === "DOCTOR" || 
      r.traineeType === "DOCTOR"
    );

    const defaultRoomType = buildingId === 2
      ? "แพทย์"
      : (buildingId === 3 ? "ห้องครอบครัว" : (buildingId === 4 ? "เจ้าหน้าที่ / นศ.ชาย" : "เจ้าหน้าที่ / นศ.หญิง"));

    const resolvedRoomType = isSpecialFamilyRoom
      ? "ห้องครอบครัว (พิเศษตามมติกรรมการบ้านพัก)"
      : (isDutyRoom
        ? "ห้องพักเวร"
        : (found ? (found.RoomType || found.roomType || defaultRoomType) : defaultRoomType));

    return {
      no: String(roomNo),
      occupied: isOccupied,
      isMaintenance: isMaintenance,
      capacity: capacity,
      isOverCapacity: occ > capacity,
      isFamilyRoom: isFamilyRoom,
      isDutyRoom: isDutyRoom,
      hasStaffResident: hasStaffResident,
      hasStudentResident: hasStudentResident,
      isTraineeMode: Boolean(isTraineeMode),
      type: resolvedRoomType,
      tenants: tenants
    };
  };

  const bPrefix = String(buildingId);
  const roomNumbersByFloor = [
    [`${bPrefix}11`, `${bPrefix}12`],
    [`${bPrefix}21`, `${bPrefix}22`, `${bPrefix}23`, `${bPrefix}24`],
    [`${bPrefix}31`, `${bPrefix}32`, `${bPrefix}33`, `${bPrefix}34`]
  ];

  const floors = roomNumbersByFloor.map((floorRooms) =>
    floorRooms.map((rNo) => getRoomInfo(rNo))
  );

  const jsonData = JSON.stringify(floors);

  // CSS Color Palettes
  let cssVarsLight = '';
  let cssVarsDark = '';

  if (isSlate) {
    // โทนสีเทาโทนเย็น (Cool Slate / Gray) สำหรับตึก 4
    cssVarsLight = `
  --sky1:#e2e8f0;--sky2:#f8fafc;--fg:#0f172a;--panel:rgba(255,255,255,.96);--muted:#475569;
  --bldg:#cbd5e1;--bldg-hl:#e2e8f0;--slab:#334155;--roof:#1e293b;--roof-top:#475569;--plinth:#0f172a;--pillar:#f8fafc;
  --grass:#86efac;
  --border-color:rgba(51,65,85,.32);
  --no-color:#0f172a;
  --no-border:#475569;
  --accent:#334155;
  --badge-bg:#e2e8f0;
  --btn-bg:#334155;
  --btn-hover:#1e293b;
`;
    cssVarsDark = `
  --sky1:#020617;--sky2:#0f172a;--fg:#f8fafc;--panel:rgba(15,23,42,.96);--muted:#94a3b8;
  --bldg:#64748b;--bldg-hl:#94a3b8;--slab:#334155;--roof:#0f172a;--roof-top:#1e293b;--plinth:#020617;--pillar:#cbd5e1;
  --grass:#166534;
  --border-color:rgba(148,163,184,.35);
  --no-color:#ffffff;
  --no-border:#94a3b8;
  --accent:#94a3b8;
  --badge-bg:#1e293b;
  --btn-bg:#334155;
  --btn-hover:#1e293b;
`;
  } else if (isCream) {
    // โทนสีครีม (Cream) สำหรับตึก 3
    cssVarsLight = `
  --sky1:#fef3c7;--sky2:#fffbeb;--fg:#0f172a;--panel:rgba(255,255,255,.96);--muted:#78350f;
  --bldg:#fef9c3;--bldg-hl:#fef08a;--slab:#b45309;--roof:#78350f;--roof-top:#d97706;--plinth:#451a03;--pillar:#ffffff;
  --grass:#86efac;
  --border-color:rgba(180,83,9,.28);
  --no-color:#78350f;
  --no-border:#b45309;
  --accent:#b45309;
  --badge-bg:#fef3c7;
  --btn-bg:#d97706;
  --btn-hover:#b45309;
`;
    cssVarsDark = `
  --sky1:#1c1917;--sky2:#292524;--fg:#fefce8;--panel:rgba(41,37,36,.96);--muted:#fef08a;
  --bldg:#fde047;--bldg-hl:#fef08a;--slab:#b45309;--roof:#78350f;--roof-top:#d97706;--plinth:#451a03;--pillar:#fefce8;
  --grass:#166534;
  --border-color:rgba(217,119,6,.35);
  --no-color:#ffffff;
  --no-border:#fde047;
  --accent:#fde047;
  --badge-bg:#78350f;
  --btn-bg:#d97706;
  --btn-hover:#b45309;
`;
  } else if (isGreen) {
    cssVarsLight = `
  --sky1:#bae6fd;--sky2:#f0fdf4;--fg:#0f172a;--panel:rgba(255,255,255,.96);--muted:#334155;
  --bldg:#bbf7d0;--bldg-hl:#86efac;--slab:#16a34a;--roof:#15803d;--roof-top:#4ade80;--plinth:#14532d;--pillar:#f0fdf4;
  --grass:#86efac;
  --border-color:rgba(21,128,61,.28);
  --no-color:#14532d;
  --no-border:#16a34a;
  --accent:#059669;
  --badge-bg:#dcfce7;
  --btn-bg:#059669;
  --btn-hover:#047857;
`;
    cssVarsDark = `
  --sky1:#064e3b;--sky2:#022c22;--fg:#ecfdf5;--panel:rgba(6,78,59,.96);--muted:#a7f3d0;
  --bldg:#10b981;--bldg-hl:#34d399;--slab:#059669;--roof:#047857;--roof-top:#10b981;--plinth:#064e3b;--pillar:#ecfdf5;
  --grass:#065f46;
  --border-color:rgba(5,150,105,.35);
  --no-color:#ffffff;
  --no-border:#10b981;
  --accent:#10b981;
  --badge-bg:#064e3b;
  --btn-bg:#10b981;
  --btn-hover:#059669;
`;
  } else {
    cssVarsLight = `
  --sky1:#bae6fd;--sky2:#f0f9ff;--fg:#0f172a;--panel:rgba(255,255,255,.96);--muted:#475569;
  --bldg:#fbcfe8;--bldg-hl:#f9a8d4;--slab:#f472b6;--roof:#f472b6;--roof-top:#f9a8d4;--plinth:#db2777;--pillar:#fdf2f8;
  --grass:#86efac;
  --border-color:rgba(157,23,77,.25);
  --no-color:#831843;
  --no-border:#9d174d;
  --accent:#be185d;
  --badge-bg:#fce7f3;
  --btn-bg:#db2777;
  --btn-hover:#be185d;
`;
    cssVarsDark = `
  --sky1:#0b1220;--sky2:#1e3a5f;--fg:#e2e8f0;--panel:rgba(22,32,51,.96);--muted:#94a3b8;
  --bldg:#f472b6;--bldg-hl:#f9a8d4;--slab:#db2777;--roof:#ec4899;--roof-top:#f472b6;--plinth:#9d174d;--pillar:#fce7f3;
  --grass:#166534;
  --border-color:rgba(236,72,153,.35);
  --no-color:#ffffff;
  --no-border:#f472b6;
  --accent:#f472b6;
  --badge-bg:#831843;
  --btn-bg:#ec4899;
  --btn-hover:#db2777;
`;
  }

  let headerTitle = "🏢 อาคาร " + buildingId;
  if (buildingName) {
    headerTitle = `🏢 ${buildingName}`;
  } else if (buildingId === 1) {
    headerTitle = "🏢 อาคาร 1 (หอพักเจ้าหน้าที่ / นศ.หญิง)";
  } else if (buildingId === 2) {
    headerTitle = "🏢 อาคาร 2 (หอพักแพทย์)";
  } else if (buildingId === 3) {
    headerTitle = "🏢 อาคาร 3 (หอพักบุคลากร / ครอบครัว)";
  } else if (buildingId === 4) {
    headerTitle = "🏢 อาคาร 4 (หอพักเจ้าหน้าที่ / นศ.ชาย)";
  }

  return `<!DOCTYPE html>
<html lang="th" data-theme="light">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover">
<title>หอพัก 3 มิติ - ${headerTitle}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Sarabun:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap" rel="stylesheet">
<style>
:root{
  ${cssVarsLight}
  box-sizing:border-box;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)
}
@media (prefers-color-scheme:dark){
  :root:not([data-theme="light"]){
    ${cssVarsDark}
  }
}
*{box-sizing:border-box;font-family:"Sarabun",sans-serif!important}
html,body{height:100%;margin:0;font-family:"Sarabun",sans-serif!important}
body{background:linear-gradient(var(--sky1),var(--sky2));color:var(--fg);font-family:"Sarabun",sans-serif!important;display:flex;flex-direction:column;overflow:hidden;user-select:none}
button,input,select,textarea{font-family:"Sarabun",sans-serif!important}
header{display:flex;flex-wrap:wrap;gap:12px;align-items:center;padding:10px 16px;background:var(--panel);z-index:10;box-shadow:0 2px 8px rgba(0,0,0,.06)}
header b{font-size:16px;color:var(--no-color);display:flex;align-items:center;gap:6px;font-family:"Sarabun",sans-serif!important;font-weight:700}
.legend{margin-left:auto;display:flex;gap:14px;font-size:13.5px;align-items:center;font-family:"Sarabun",sans-serif!important;font-weight:600}
.legend i{display:inline-block;width:14px;height:14px;border-radius:3px;margin-right:5px;vertical-align:-2px;border:1.5px solid #fff}
.legend i.on{background:linear-gradient(135deg,#fef08a,#f59e0b);box-shadow:0 0 8px #fbbf24}
.legend i.off{background:linear-gradient(135deg,#64748b,#475569)}
.legend i.maint{background:linear-gradient(135deg,#94a3b8,#475569)}
#info{padding:8px 16px;font-size:14px;min-height:36px;font-weight:600;color:var(--muted);text-align:center;font-family:"Sarabun",sans-serif!important}
#stage{flex:1;perspective:1800px;display:flex;align-items:center;justify-content:center;cursor:grab;touch-action:none}
#building{position:relative;width:0;height:0;transform-style:preserve-3d;transform:rotateX(-22deg) rotateY(-35deg)}
.floor,.box{position:absolute;left:0;top:0;transform-style:preserve-3d}
.floor{transition:opacity .25s,transform .45s ease;transform:translate3d(0,calc(var(--y,0px) - var(--lift,0px)),0)}
.roofgrp,.roofgrp *{pointer-events:none}
#building:has(.floor:hover) .floor:not(:hover) .face{opacity:.25}
#building:has(.floor:hover) .roofgrp{opacity:.55}
.room.ghost{pointer-events:none}
.room.ghost .face{opacity:.15}
.room.hov .face{box-shadow:inset 0 0 0 4px #fff}
.floor:has(.room.hov) .room:not(.hov) .face{opacity:.38}
.floor:has(.room.hov) .room.ghost .face{opacity:.15}

${
  showCats
    ? `/* แมวเฉพาะตึก 1 */
.cat{position:absolute;left:0;top:0;width:120px;height:90px;margin:-90px 0 0 -60px;transform-origin:60px 90px;pointer-events:none}
.cat *{pointer-events:none}
.cat svg{width:100%;height:100%;overflow:visible;animation:bob var(--spd,.34s) ease-in-out infinite}
.hip,.knee{transform-box:view-box;animation-duration:calc(var(--spd,.34s)*2);animation-iteration-count:infinite;animation-timing-function:ease-in-out}
.hip{animation-name:hip}
.knee{animation-name:knee}
.hip.leg2,.hip.leg2 .knee{animation-delay:calc(var(--spd,.34s)*-1)}
.tail{transform-box:fill-box;transform-origin:100% 100%;animation:wag .5s ease-in-out infinite alternate}
.cat.white svg{filter:drop-shadow(0 0 1.5px rgba(71,85,105,.6))}
.cat.stop svg,.cat.stop .hip,.cat.stop .knee{animation:none}
@keyframes bob{50%{transform:translateY(-4px)}}
@keyframes hip{0%{transform:rotate(-30deg)}50%{transform:rotate(30deg)}100%{transform:rotate(-30deg)}}
@keyframes knee{0%,50%{transform:rotate(0)}75%{transform:rotate(55deg)}100%{transform:rotate(0)}}
@keyframes wag{from{transform:rotate(-12deg)}to{transform:rotate(14deg)}}
`
    : (showDogAndBike
        ? `/* ตึก 4: มอเตอร์ไซค์สีดำและหมาตัวสีดำวิ่งไล่ (ขนาดสมส่วนเท่าแมว ล้อหมุนนิ่งตรงแกน หางและขาติดสนิท) */
.moto-runner{position:absolute;left:0;top:0;width:135px;height:85px;margin:-75px 0 0 -67px;transform-origin:67px 75px;pointer-events:none;will-change:transform}
.moto-runner *{pointer-events:none}
.moto-runner svg{width:100%;height:100%;overflow:visible}
.spin-wheel-r{transform-origin:30px 58px;animation:spinRoll 1.1s linear infinite}
.spin-wheel-f{transform-origin:102px 58px;animation:spinRoll 1.1s linear infinite}
@keyframes spinRoll{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
.exhaust-puff{transform-origin:12px 62px;animation:puff .9s ease-out infinite}
@keyframes puff{0%{opacity:.75;transform:scale(.6) translateX(0)}100%{opacity:0;transform:scale(2) translateX(-18px)}}

.dog-chaser{position:absolute;left:0;top:0;width:120px;height:85px;margin:-72px 0 0 -60px;transform-origin:60px 72px;pointer-events:none;will-change:transform}
.dog-chaser *{pointer-events:none}
.dog-chaser svg{width:100%;height:100%;overflow:visible;animation:dogBodyBob .38s ease-in-out infinite alternate}
@keyframes dogBodyBob{0%{transform:translateY(0) rotate(-1deg)}100%{transform:translateY(-4px) rotate(2deg)}}
.dog-hip-b1{transform-origin:32px 38px;animation:dogHipB1 .38s ease-in-out infinite alternate}
.dog-hip-b2{transform-origin:34px 38px;animation:dogHipB2 .38s ease-in-out infinite alternate}
.dog-hip-f1{transform-origin:74px 40px;animation:dogHipF1 .38s ease-in-out infinite alternate}
.dog-hip-f2{transform-origin:76px 40px;animation:dogHipF2 .38s ease-in-out infinite alternate}
@keyframes dogHipB1{0%{transform:rotate(32deg)}100%{transform:rotate(-32deg)}}
@keyframes dogHipB2{0%{transform:rotate(-30deg)}100%{transform:rotate(32deg)}}
@keyframes dogHipF1{0%{transform:rotate(-32deg)}100%{transform:rotate(30deg)}}
@keyframes dogHipF2{0%{transform:rotate(28deg)}100%{transform:rotate(-32deg)}}
.dog-tail{transform-origin:32px 36px;animation:dogTailWag .22s ease-in-out infinite alternate}
@keyframes dogTailWag{0%{transform:rotate(-24deg)}100%{transform:rotate(24deg)}}
.dog-bark{position:absolute;top:-16px;right:4px;background:#ffffff;color:#0f172a;font-size:10px;font-weight:800;padding:2px 7px;border-radius:10px;border:1.5px solid #0f172a;box-shadow:0 2px 5px rgba(0,0,0,.35);animation:barkPop 2.8s ease-in-out infinite;white-space:nowrap;pointer-events:none}
@keyframes barkPop{0%,70%,100%{opacity:0;transform:scale(.6) translateY(4px)}80%,94%{opacity:1;transform:scale(1) translateY(0)}}
`
        : `/* ตึก 2, 3: ไม่มีสัตว์เลี้ยง */`)
}

.ground{position:absolute;left:0;top:0;width:1100px;height:900px;margin:-450px 0 0 -550px;border-radius:40px;
 background:radial-gradient(ellipse at center,rgba(0,0,0,.28) 0,rgba(0,0,0,.28) 20%,transparent 38%),var(--grass)}

/* สีตึกตามโทนที่กำหนด */
.room{--c:var(--bldg);cursor:pointer}
.floor:hover .room{--c:var(--bldg-hl)}
.slab{--c:var(--slab)}.roof{--c:var(--roof)}.roof-slope{--c:var(--roof-top)}.plinth{--c:var(--plinth)}.pillar{--c:var(--pillar)}

.face{position:absolute;left:50%;top:50%;background:linear-gradient(rgba(255,255,255,.18),rgba(0,0,0,.06)),var(--c);border:1px solid var(--border-color);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;font-size:12px;font-weight:700;color:#fff;backface-visibility:hidden;transition:background-color .2s,opacity .2s;overflow:hidden}
.face.f{width:var(--w);height:var(--h);margin:calc(var(--h)/-2) 0 0 calc(var(--w)/-2);transform:translateZ(calc(var(--d)/2));transition:transform .55s cubic-bezier(.34,1.25,.64,1),opacity .35s ease}
.room.open .face.f,.room.sel .face.f,#building.bld-cutaway .room .face.f,.room:hover .face.f,.room.hov .face.f,.floor:hover .room:hover .face.f{
  transform:translateZ(calc(var(--d)/2 + 20px)) translateY(-26px) scale(.92);
  opacity:0;
  pointer-events:none
}
.face.b{width:var(--w);height:var(--h);margin:calc(var(--h)/-2) 0 0 calc(var(--w)/-2);transform:rotateY(180deg) translateZ(calc(var(--d)/2));filter:brightness(.82)}
.face.r{width:var(--d);height:var(--h);margin:calc(var(--h)/-2) 0 0 calc(var(--d)/-2);transform:rotateY(90deg) translateZ(calc(var(--w)/2));filter:brightness(.9)}
.face.l{width:var(--d);height:var(--h);margin:calc(var(--h)/-2) 0 0 calc(var(--d)/-2);transform:rotateY(-90deg) translateZ(calc(var(--w)/2));filter:brightness(.86)}
.face.t{width:var(--w);height:var(--d);margin:calc(var(--d)/-2) 0 0 calc(var(--w)/-2);transform:rotateX(90deg) translateZ(calc(var(--h)/2));filter:brightness(1.08);transition:opacity .35s ease}
.room.open .face.t,.room.sel .face.t,#building.bld-cutaway .room .face.t,#building.exploded .room .face.t,.room:hover .face.t,.room.hov .face.t,.floor:hover .room .face.t,.floor:hover .face.t{
  opacity:0;
  pointer-events:none
}
#building.exploded .roofgrp{opacity:.2;transition:opacity .35s ease}
#building.bld-cutaway .roofgrp{opacity:0;pointer-events:none}
.face.bt{width:var(--w);height:var(--d);margin:calc(var(--d)/-2) 0 0 calc(var(--w)/-2);transform:rotateX(-90deg) translateZ(calc(var(--h)/2));filter:brightness(.7)}

/* หลังคาทรงจั่ว */
.roof-panel{position:absolute;left:50%;top:50%;background:repeating-linear-gradient(90deg,rgba(255,255,255,.18) 0 6px,rgba(0,0,0,.05) 6px 12px),linear-gradient(180deg,var(--roof-top),var(--roof));border:2px solid var(--border-color);border-radius:4px}
.gable{position:absolute;left:50%;top:50%;width:0;height:0;border-style:solid;filter:brightness(.88)}

/* หน้าต่าง & ดวงไฟ (2 ดวง: แยกซ้าย-ขวา ตามจำนวนผู้พักต่อเตียง) พร้อมจำลองห้องภายใน (เตียง ทีวี คนเดิน/นอน/ดูทีวี) */
.win{width:58%;height:37%;border:2.5px solid #ffffff;border-radius:4px;background:#1e293b;position:relative;display:flex;overflow:hidden;box-shadow:0 2px 7px rgba(0,0,0,.3);transition:transform .25s ease,box-shadow .25s ease}
.room.hov .win{transform:scale(1.35) translateZ(6px);box-shadow:0 6px 16px rgba(0,0,0,.55);z-index:30}
.win .pane{flex:1;height:100%;position:relative;overflow:hidden;transition:background .25s ease,box-shadow .25s ease}
.win .pane:first-child{border-right:1px solid rgba(255,255,255,.6)}
.win .pane:last-child{border-left:1px solid rgba(255,255,255,.6)}

/* ดวงไฟที่ดับ (มืดสนิท) */
.win .pane.dark{background:linear-gradient(180deg,#1e293b,#0f172a);box-shadow:inset 0 0 6px rgba(0,0,0,.7)}
/* ดวงไฟที่เปิด (สว่างเรืองรองสีเหลืองทองอบอุ่น) */
.win .pane.lit{background:radial-gradient(ellipse at 50% 30%,#fef9c3 0%,#fef08a 45%,#fbbf24 100%);box-shadow:inset 0 0 4px #fef08a,0 0 12px rgba(251,191,36,.7)}

/* พื้นห้องภายใน */
.p-floor{position:absolute;bottom:0;left:0;right:0;height:4px;background:#d97706;border-top:1px solid #92400e;z-index:1}
.pane.dark .p-floor{background:#334155;border-top:1px solid #1e293b}

/* เตียงนอน 2 ฝั่ง */
.p-bed{position:absolute;bottom:3px;width:13px;height:9px;background:#f8fafc;border:1px solid #cbd5e1;border-radius:2px;z-index:2;box-shadow:0 1px 2px rgba(0,0,0,.25)}
.p-bed.l{left:2px}
.p-bed.r{right:2px}
.p-bed::before{content:"";position:absolute;top:1px;width:3.5px;height:7px;background:#fff;border-radius:1px;border:1px solid #e2e8f0}
.p-bed.l::before{left:1px}
.p-bed.r::before{right:1px}
.p-bed::after{content:"";position:absolute;top:0;bottom:0;width:7.5px;border-radius:1px}
.p-bed.l::after{right:0;background:#0284c7}
.p-bed.r::after{left:0;background:#ec4899}
.pane.dark .p-bed{opacity:.35;filter:grayscale(.8)}

/* โทรทัศน์ติดผนังพร้อมแสงกะพริบ */
.p-tv{position:absolute;top:2.5px;right:2px;width:10px;height:7px;background:#0f172a;border:1px solid #475569;border-radius:1.5px;z-index:5;overflow:hidden;box-shadow:0 0 4px rgba(56,189,248,.7)}
.p-tv-screen{width:100%;height:100%;animation:tvGlow 2.5s infinite alternate}
@keyframes tvGlow{0%{background:#38bdf8;opacity:.9}25%{background:#818cf8;opacity:.7}50%{background:#c084fc;opacity:1}75%{background:#34d399;opacity:.8}100%{background:#fbbf24;opacity:.85}}
.pane.dark .p-tv{box-shadow:none;border-color:#334155}
.pane.dark .p-tv-screen{animation:none;background:#1e293b}

/* คนเดินในห้อง */
.p-walker{position:absolute;bottom:3px;left:2px;width:9px;height:14px;z-index:4;transform-origin:bottom center;animation:pWalk 5.5s cubic-bezier(.45,0,.55,1) infinite alternate;will-change:transform}
.p-walker svg{width:100%;height:100%;display:block;animation:pBob .4s ease-in-out infinite alternate}
@keyframes pWalk{
  0%   { transform: translateX(0) scaleX(1); }
  44%  { transform: translateX(11px) scaleX(1); }
  48%  { transform: translateX(11px) scaleX(0.05); }
  52%  { transform: translateX(11px) scaleX(-1); }
  94%  { transform: translateX(0) scaleX(-1); }
  97%  { transform: translateX(0) scaleX(-0.05); }
  100% { transform: translateX(0) scaleX(1); }
}
@keyframes pBob{from{transform:translateY(0)}to{transform:translateY(-1.5px)}}

/* คนนอนบนเตียงพร้อม Zzz */
.p-sleeper{position:absolute;bottom:3.5px;width:12px;height:8px;z-index:3;animation:pSleep 2s ease-in-out infinite alternate}
.p-sleeper.l{left:2px}
.p-sleeper.r{right:2px}
.p-sleeper svg{width:100%;height:100%;display:block}
@keyframes pSleep{from{transform:scale(1)}to{transform:scale(1.08)}}
.p-zzz{position:absolute;font-size:7px;font-weight:900;color:#2563eb;animation:pZzz 2s infinite linear;opacity:0;pointer-events:none;z-index:6;font-family:sans-serif}
@keyframes pZzz{0%{transform:translate(0,0) scale(.5);opacity:0}30%{opacity:1}80%{transform:translate(3px,-9px) scale(1.1);opacity:.8}100%{transform:translate(5px,-13px) scale(1.3);opacity:0}}

/* คนนั่งดูทีวี */
.p-watcher{position:absolute;bottom:3px;left:7px;width:8px;height:12px;z-index:4;animation:pWatch 3s ease-in-out infinite alternate}
.p-watcher svg{width:100%;height:100%;display:block}
@keyframes pWatch{0%,30%{transform:translateY(0) rotate(0)}50%{transform:translateY(-1px) rotate(3deg)}70%{transform:translateY(0) rotate(-2deg)}100%{transform:translateY(-1px) rotate(2deg)}}
.p-act-icon{position:absolute;top:-5px;right:-5px;font-size:7px;line-height:1;filter:drop-shadow(0 1px 2px rgba(0,0,0,.6));pointer-events:none}

.side-wins{display:flex;gap:14%;width:72%;height:37%;justify-content:center}
.side-wins .win{width:44%;height:100%}

.face.f::after{content:"";position:absolute;left:0;right:0;bottom:0;height:10px;background:repeating-linear-gradient(90deg,rgba(255,255,255,.85) 0 2px,transparent 2px 9px);border-top:2px solid rgba(255,255,255,.9)}
.no{background:#fff;color:var(--no-color);font-size:17px;font-weight:800;line-height:1;padding:3px 10px;border-radius:5px;border:2px solid var(--no-border);margin-bottom:8px;letter-spacing:.5px}
.tag{font-size:10px;color:#fff;font-weight:700;text-shadow:0 1px 2px rgba(0,0,0,.25)}
.room.sel .face{border:3px solid #fff;box-shadow:inset 0 0 0 4px var(--accent)}

/* Hitbox target planes to prevent mouseleave flickering when room faces animate */
.hitbox{position:absolute;left:50%;top:50%;opacity:0;cursor:pointer;pointer-events:auto!important;z-index:45}
.hitbox.front{width:var(--w);height:var(--h);margin:calc(var(--h)/-2) 0 0 calc(var(--w)/-2);transform:translateZ(calc(var(--d)/2 + 2px))}
.hitbox.top{width:var(--w);height:var(--d);margin:calc(var(--d)/-2) 0 0 calc(var(--w)/-2);transform:rotateX(90deg) translateZ(calc(var(--h)/2 + 2px))}

/* ป้ายลอยแสดงรูปวงกลมคนพักเมื่อชี้ห้อง */
#hoverCard{
  position:absolute;left:0;top:0;pointer-events:none!important;
  background:var(--panel);color:var(--fg);padding:10px 14px;border-radius:16px;
  box-shadow:0 10px 28px rgba(15,23,42,.28),0 0 0 2px var(--accent);
  display:flex;flex-direction:column;align-items:center;gap:8px;
  min-width:160px;max-width:340px;backdrop-filter:blur(8px);z-index:90;
  transition:opacity .15s ease;opacity:0
}
#hoverCard.show{opacity:1;pointer-events:none!important}
#hoverCard *{pointer-events:none!important}
#hoverCard .hc-title{font-size:13px;font-weight:800;color:var(--no-color);background:var(--badge-bg);padding:3px 12px;border-radius:99px;text-align:center}
#hoverCard .hc-list{display:flex;gap:10px;justify-content:center;align-items:flex-start;flex-wrap:wrap;max-width:320px}
.person{display:flex;flex-direction:column;align-items:center;gap:3px;max-width:96px;text-align:center}
.avatar{width:44px;height:44px;border-radius:50%;border:2px solid var(--accent);box-shadow:0 2px 6px rgba(0,0,0,.15);overflow:hidden;background:#fdf2f8;flex-shrink:0;display:flex;align-items:center;justify-content:center}
.avatar img{width:100%;height:100%;object-fit:cover;border-radius:50%;display:block}
.avatar svg{width:100%;height:100%;display:block}
.person-name{font-size:11px;font-weight:700;line-height:1.2;color:var(--fg);word-break:break-word}
.fam-badge{font-size:9.5px;font-weight:700;color:#92400e;background:#fef3c7;border:1px solid #fde047;padding:1px 5px;border-radius:6px;line-height:1.2;display:inline-block}
.role-badge{font-size:9px;font-weight:600;color:#475569;background:#f1f5f9;padding:1px 5px;border-radius:6px;line-height:1.2;display:inline-block}
.hc-warn{margin-top:4px;padding:5px 8px;background:#fef2f2;border:1.5px solid #ef4444;border-radius:8px;color:#b91c1c;font-size:11px;font-weight:800;text-align:center;line-height:1.35;width:100%}
.room-subtag{font-size:8.5px;font-weight:800;padding:1px 5px;border-radius:4px;line-height:1.1;margin-top:-3px;letter-spacing:0}
.room-subtag.duty{background:#e0f2fe;color:#0369a1;border:1px solid #7dd3fc}
.room-subtag.fam{background:#fef3c7;color:#92400e;border:1px solid #fde047}
.hc-subtag{font-size:10.5px;font-weight:800;padding:2px 8px;border-radius:6px;margin-top:2px}
.hc-subtag.duty{background:#e0f2fe;color:#0369a1;border:1px solid #7dd3fc}
.hc-subtag.fam{background:#fef3c7;color:#92400e;border:1px solid #fde047}
.vacant-badge{font-size:12px;font-weight:700;color:#059669;background:#dcfce7;border:1px solid #86efac;padding:4px 10px;border-radius:12px}

/* แถบด้านล่างเมื่อคลิกห้อง */
#panel{position:fixed;left:50%;transform:translateX(-50%);bottom:calc(16px + env(safe-area-inset-bottom,0px));background:var(--panel);color:var(--fg);padding:10px 18px;border-radius:14px;box-shadow:0 6px 24px rgba(0,0,0,.25),0 0 0 2px var(--accent);display:flex;gap:14px;align-items:center;font-size:14px;z-index:20;flex-wrap:wrap;justify-content:center}
#panel[hidden]{display:none}
.p-avatars{display:flex;gap:8px;align-items:center}
.p-avatars .avatar{width:34px;height:34px;border-width:2px}
.p-btn{padding:7px 14px;border-radius:8px;border:0;font-weight:700;cursor:pointer;font-size:13px;display:inline-flex;align-items:center;gap:5px;transition:all .15s ease}
.p-btn-manage{background:var(--btn-bg);color:#fff}
.p-btn-manage:hover{background:var(--btn-hover)}
.p-btn-checkin{background:#059669;color:#fff}
.p-btn-checkin:hover{background:#047857}
.p-warn{color:#dc2626;font-weight:800}

/* 3D Room Interior Styles (ผ่าขวาง 3 มิติ เห็นข้างในห้อง) */
.interior{position:absolute;left:0;top:0;width:0;height:0;transform-style:preserve-3d;pointer-events:none}
.in-floor{position:absolute;left:50%;top:50%;width:var(--w);height:var(--d);margin:calc(var(--d)/-2) 0 0 calc(var(--w)/-2);transform:rotateX(90deg) translateZ(calc(var(--h)/-2 + 1px));background:repeating-linear-gradient(90deg,#d97706 0 16px,#b45309 16px 17px),#b45309;border:1px solid #78350f;box-shadow:inset 0 0 35px rgba(0,0,0,.45);overflow:visible;transform-style:preserve-3d}
.in-back{position:absolute;left:50%;top:50%;width:var(--w);height:var(--h);margin:calc(var(--h)/-2) 0 0 calc(var(--w)/-2);transform:translateZ(calc(var(--d)/-2 + 1px));border:1px solid rgba(0,0,0,.15);box-shadow:inset 0 0 25px rgba(0,0,0,.3);display:flex;flex-direction:column;align-items:center;justify-content:flex-start;padding:5px 6px;overflow:hidden}
.in-left{position:absolute;left:50%;top:50%;width:var(--d);height:var(--h);margin:calc(var(--h)/-2) 0 0 calc(var(--d)/-2);transform:rotateY(90deg) translateZ(calc(var(--w)/-2 + 1px));background:linear-gradient(180deg,rgba(254,243,199,.9),rgba(253,230,138,.95));box-shadow:inset 0 0 25px rgba(0,0,0,.25)}
.in-right{position:absolute;left:50%;top:50%;width:var(--d);height:var(--h);margin:calc(var(--h)/-2) 0 0 calc(var(--d)/-2);transform:rotateY(-90deg) translateZ(calc(var(--w)/-2 + 1px));background:linear-gradient(180deg,rgba(254,243,199,.9),rgba(253,230,138,.95));box-shadow:inset 0 0 25px rgba(0,0,0,.25)}
.in-rail{position:absolute;left:50%;top:50%;width:var(--w);height:14px;margin:calc(var(--h)/2 - 14px) 0 0 calc(var(--w)/-2);transform:translateZ(calc(var(--d)/2));background:repeating-linear-gradient(90deg,rgba(255,255,255,.85) 0 2px,transparent 2px 8px),linear-gradient(180deg,rgba(255,255,255,.6),rgba(255,255,255,.2));border-top:2px solid #fff;z-index:5;pointer-events:none}

.b-tv{width:42px;height:24px;background:#0f172a;border-radius:3px;border:1.5px solid #475569;display:flex;align-items:center;justify-content:center;color:#38bdf8;font-size:7.5px;font-weight:800;letter-spacing:.5px}
.b-tv.lit{animation:tvGlow 2.5s infinite alternate;box-shadow:0 0 10px rgba(56,189,248,.8)}
.b-tv.dark{background:#1e293b;color:#64748b;border-color:#334155;box-shadow:none}
.in-title{font-size:8px;font-weight:800;color:#78350f;background:rgba(255,255,255,.9);padding:1px 5px;border-radius:4px;margin-top:2px;box-shadow:0 1px 3px rgba(0,0,0,.15);white-space:nowrap}

.f-bed{position:absolute;width:35px;height:66px;background:#f8fafc;border:1px solid #cbd5e1;border-radius:4px;box-shadow:0 2px 5px rgba(0,0,0,.3);overflow:hidden}
.f-bed.l{left:6px;bottom:12px}
.f-bed.r{right:6px;bottom:12px}
.f-bed .pillow{position:absolute;top:3px;left:50%;transform:translateX(-50%);width:23px;height:11px;background:#fff;border:1px solid #e2e8f0;border-radius:2px}
.f-bed .blanket{position:absolute;bottom:0;left:0;right:0;height:44px;border-radius:0 0 3px 3px}
.f-bed.l .blanket{background:#0284c7}
.f-bed.r .blanket{background:#ec4899}
.f-bed.vac{opacity:.35;filter:grayscale(.8)}
.bed-sleep{position:absolute;bottom:6px;left:50%;transform:translateX(-50%);display:flex;align-items:center;justify-content:center;z-index:4}

.f-rug{position:absolute;left:50%;top:50%;transform:translate(-50%,-40%);width:44px;height:58px;border-radius:6px;box-shadow:0 0 6px rgba(0,0,0,.25)}
.f-item{position:absolute;display:flex;align-items:center;justify-content:center;font-size:15px;filter:drop-shadow(0 2px 3px rgba(0,0,0,.3))}
/* Upright 3D Standing Human Characters (มองเห็นยืนเด่นชัดทั้งจากด้านหน้าและด้านบน) */
.char-3d{position:absolute;width:22px;height:34px;left:50%;top:50%;margin-left:-11px;margin-top:-17px;transform-style:preserve-3d;pointer-events:none;z-index:20}
.char-3d svg{width:100%;height:100%;display:block;filter:drop-shadow(0 2px 5px rgba(0,0,0,.5))}
.char-3d::after{content:"";position:absolute;bottom:-2px;left:50%;transform:translateX(-50%) rotateX(90deg);width:16px;height:7px;border-radius:50%;background:rgba(0,0,0,.4)}

.char-walk{animation:pWalk3D 5.5s cubic-bezier(.45,0,.55,1) infinite alternate;will-change:transform}
.char-walk.r{animation:pWalk3D2 6s cubic-bezier(.45,0,.55,1) infinite alternate;will-change:transform}
@keyframes pWalk3D{
  0%    { transform: translate3d(-16px, 16px, 15px) rotateX(-12deg) scaleX(1); }
  44%   { transform: translate3d(16px, 16px, 15px) rotateX(-12deg) scaleX(1); }
  48%   { transform: translate3d(16px, 16px, 15px) rotateX(-12deg) scaleX(0.05); }
  52%   { transform: translate3d(16px, 16px, 15px) rotateX(-12deg) scaleX(-1); }
  94%   { transform: translate3d(-16px, 16px, 15px) rotateX(-12deg) scaleX(-1); }
  97%   { transform: translate3d(-16px, 16px, 15px) rotateX(-12deg) scaleX(-0.05); }
  100%  { transform: translate3d(-16px, 16px, 15px) rotateX(-12deg) scaleX(1); }
}
@keyframes pWalk3D2{
  0%    { transform: translate3d(16px, 16px, 4px) rotateX(-12deg) scaleX(-1); }
  44%   { transform: translate3d(-10px, 16px, 4px) rotateX(-12deg) scaleX(-1); }
  48%   { transform: translate3d(-10px, 16px, 4px) rotateX(-12deg) scaleX(-0.05); }
  52%   { transform: translate3d(-10px, 16px, 4px) rotateX(-12deg) scaleX(1); }
  94%   { transform: translate3d(16px, 16px, 4px) rotateX(-12deg) scaleX(1); }
  97%   { transform: translate3d(16px, 16px, 4px) rotateX(-12deg) scaleX(0.05); }
  100%  { transform: translate3d(16px, 16px, 4px) rotateX(-12deg) scaleX(-1); }
}

.char-act{position:absolute;display:flex;flex-direction:column;align-items:center;animation:actBob 3s ease-in-out infinite alternate;will-change:transform}
.char-act.l{left:40%;}
.char-act.r{left:62%;animation-delay:-1.5s;}
@keyframes actBob{
  0%   { transform: translate3d(0, 16px, -18px) rotateX(-12deg) translateY(0); }
  50%  { transform: translate3d(0, 16px, -18px) rotateX(-12deg) translateY(-2px); }
  100% { transform: translate3d(0, 16px, -18px) rotateX(-12deg) translateY(0); }
}
.char-act-badge{position:absolute;top:6px;right:-10px;font-size:14px;filter:drop-shadow(0 2px 4px rgba(0,0,0,.5));animation:badgePop 1.8s infinite alternate ease-in-out;will-change:transform}
@keyframes badgePop{from{transform:scale(.9) rotate(-6deg)}to{transform:scale(1.15) rotate(6deg)}}
.char-act-label{font-size:7.5px;font-weight:800;background:rgba(15,23,42,.88);color:#fef08a;padding:1px 5px;border-radius:3px;margin-top:2px;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,.3)}

.char-bed{position:absolute;transform-style:preserve-3d;display:flex;align-items:center;gap:2px;z-index:15;will-change:transform}
.char-bed.l{transform:translate3d(-34px, 16px, 35px) rotateX(-10deg)}
.char-bed.r{transform:translate3d(34px, 16px, 35px) rotateX(-10deg)}
.char-bed svg{width:24px;height:17px;display:block;filter:drop-shadow(0 2px 3px rgba(0,0,0,.35))}
.char-bed .p-zzz{position:absolute;font-size:8px;font-weight:900;color:#2563eb;animation:pZzz 2s infinite linear;opacity:0}

.btn-dollhouse{background:var(--btn-bg);color:#fff;border:1.5px solid rgba(255,255,255,.4);padding:4px 12px;border-radius:99px;font-size:12px;font-weight:700;cursor:pointer;display:inline-flex;align-items:center;gap:5px;box-shadow:0 2px 6px rgba(0,0,0,.15);transition:all .2s ease;margin-right:8px}
.btn-dollhouse:hover{background:var(--btn-hover);transform:translateY(-1px)}
.btn-dollhouse.active{background:#059669;border-color:#34d399;box-shadow:0 0 10px rgba(16,185,129,.5)}
</style>
</head>
<body>
<header>
  <b>${headerTitle}</b>
  <div class="legend">
    <button id="btnDollhouse" class="btn-dollhouse" onclick="toggleDollhouse()">🏠 เปิดห้องดูข้างใน 3D</button>
    <span><i class="on"></i>มีคนพัก</span>
    <span><i class="off"></i>ห้องว่าง</span>
  </div>
</header>
<div id="info">ชี้ที่ห้องเพื่อดูผู้เข้าพัก • ชี้ที่ชั้นเพื่อแยกชั้น 3D • <b>คลิกที่ห้องเพื่อจัดการห้องพักทันที</b> • ลากเพื่อหมุนตึก 360°</div>
<div id="stage">
  <div id="building">
    <div id="hoverCard"></div>
  </div>
</div>
<div id="panel" hidden>
  <div class="p-avatars" id="pavatars"></div>
  <span id="ptext"></span>
  <button id="paction" class="p-btn p-btn-manage">⚙️ จัดการห้องนี้</button>
</div>

<script>
let DATA = ${jsonData};

function makeNormalHumanAvatarSVG(t, idx){
  idx = idx || 0;
  var bgColors = ['#fef3c7', '#dcfce7', '#e0e7ff', '#fce7f3', '#fed7aa'];
  var bg = bgColors[idx % bgColors.length];
  return '<svg viewBox="0 0 64 64" style="width:100%;height:100%;display:block">' +
    '<circle cx="32" cy="32" r="32" fill="' + bg + '"/>' +
    '<path d="M12 60 C12 46, 52 46, 52 60 Z" fill="#3b82f6"/>' +
    '<rect x="27" y="38" width="10" height="9" rx="3" fill="#fed7aa"/>' +
    '<circle cx="32" cy="27" r="14" fill="#fed7aa"/>' +
    '<path d="M18 25 C18 13, 46 13, 46 25 C46 23, 44 17, 32 17 C20 17, 18 23, 18 25 Z" fill="#334155"/>' +
    '<circle cx="27" cy="27" r="1.8" fill="#0f172a"/>' +
    '<circle cx="37" cy="27" r="1.8" fill="#0f172a"/>' +
    '<circle cx="27.6" cy="26.4" r="0.6" fill="#fff"/>' +
    '<circle cx="37.6" cy="26.4" r="0.6" fill="#fff"/>' +
    '<ellipse cx="24" cy="30.5" rx="2.2" ry="1.2" fill="#fb7185" opacity="0.6"/>' +
    '<ellipse cx="40" cy="30.5" rx="2.2" ry="1.2" fill="#fb7185" opacity="0.6"/>' +
    '<path d="M29 32 Q32 35 35 32" stroke="#b45309" stroke-width="1.6" fill="none" stroke-linecap="round"/>' +
  '</svg>';
}

function handleAvatarError(img, idx){
  if (!img) return;
  img.onerror = null;
  const parent = img.parentElement;
  if (parent) {
    parent.innerHTML = makeNormalHumanAvatarSVG({}, idx || 0);
  }
}

function renderAvatar(t, idx){
  idx = idx || 0;
  if (t && t.avatarUrl && !t.isFamily) {
    return '<img src="' + t.avatarUrl + '" alt="' + (t.name || '') + '" style="width:100%;height:100%;object-fit:cover;object-position:center top;border-radius:50%;display:block" onerror="handleAvatarError(this, ' + idx + ')" />';
  }
  return makeNormalHumanAvatarSVG(t, idx);
}

function onRoomClick(room, floorIndex){
  window.parent.postMessage({
    type: 'DORM_3D_ROOM_CLICK',
    buildingId: ${buildingId},
    roomNo: room.no,
    floor: floorIndex + 1,
    isOccupied: room.occupied
  }, '*');
}

const W = 108, H = 66, D = 196, GAP = 4, SL = 10;
const $ = id => document.getElementById(id);
const bld = $('building');
let selR = null, selEl = null, selF = 0;
let floorEls = [], floorRooms = [], roofGrp = null, collapseT = null;
let geo = null, hovEl = null, hovData = null, hovTimer = null;
const EX = 84;

function setHov(el, info){
  if (hovEl === el) return;
  if (hovEl) hovEl.classList.remove('hov');
  hovEl = el;
  hovData = info || null;
  if (el) el.classList.add('hov');
  updateHoverCard();
}

function updateHoverCard(){
  const card = $('hoverCard');
  if (!card) return;
  if (!hovData){
    card.classList.remove('show');
    return;
  }
  const { r, f, x, z } = hovData;
  const n = DATA.length, step = H + SL;
  const yOf = fl => -(fl - (n-1)/2) * step;
  const lift = isExploded ? f * EX : 0;

  // สำหรับชั้น 3 (f >= 2) ซึ่งอยู่บนสุด หากให้การ์ดลอยขึ้นจะล้นขอบจอ ให้การ์ดตกลงมาอยู่ด้านล่างห้อง
  const isFloor3 = (f >= 2);
  const y = isFloor3 ? (yOf(f) - lift + H/2 + 16) : (yOf(f) - lift - H/2 - 12);
  const yShift = isFloor3 ? '0%' : '-100%';

  let content = '';
  if (r.isTraineeMode && r.hasStaffResident) {
    const items = (r.tenants && r.tenants.length ? r.tenants : [{ name: 'เจ้าหน้าที่', role: 'บุคลากร' }]).map((t, idx) => \`
      <div class="person">
        <div class="avatar">\${renderAvatar(t, idx)}</div>
        <div class="person-name">\${t.name}</div>
        <span class="role-badge" style="background:#334155;color:#fff">เจ้าหน้าที่</span>
      </div>
    \`).join('');
    content = \`<div class="hc-title" style="background:#1e293b;color:#f8fafc">ห้อง \${r.no} • 🔒 เจ้าหน้าที่พักอยู่ (ดูอย่างเดียว)</div><div class="hc-list">\${items}</div><div class="hc-subtag" style="background:#f8fafc;color:#64748b;border:1px solid #cbd5e1;padding:4px;font-size:10px;text-align:center">สงวนสิทธิ์สำหรับเจ้าหน้าที่ (ไม่สามารถจัดการในหน้านักศึกษาได้)</div>\`;
  } else if (r.occupied && r.tenants && r.tenants.length){
    const items = r.tenants.map((t, idx) => \`
      <div class="person">
        <div class="avatar">\${renderAvatar(t, idx)}</div>
        <div class="person-name">\${t.name}</div>
        \${t.isFamily ? '<span class="fam-badge">ครอบครัว</span>' : '<span class="role-badge">' + (t.role || 'ผู้พัก') + '</span>'}
      </div>
    \`).join('');
    let overCapHtml = '';
    if (r.tenants.length > r.capacity) {
      overCapHtml = \`<div class="hc-warn">⚠️ พักเกินจำนวนเตียง (\${r.tenants.length}/\${r.capacity} เตียง)<br><span style="font-size:9.5px;font-weight:600">กรุณาเพิ่มจำนวนเตียงในระบบ</span></div>\`;
    }
    let subtagHtml = '';
    if (r.isDutyRoom) {
      subtagHtml = \`<div class="hc-subtag duty">🛏️ ห้องพักเวร</div>\`;
    } else if (r.isFamilyRoom && ${buildingId} === 4) {
      subtagHtml = \`<div class="hc-subtag fam">👨‍👩‍👧 ห้องครอบครัวพิเศษ (ตามมติกรรมการบ้านพัก)</div>\`;
    }
    const isFull = r.tenants.length >= r.capacity;
    const statusText = isFull ? 'เต็ม' : 'มีเตียงว่าง';
    content = \`<div class="hc-title">ห้อง \${r.no} • \${statusText} (\${r.tenants.length}/\${r.capacity} คน)</div>\${subtagHtml}<div class="hc-list">\${items}</div>\${overCapHtml}\`;
  } else if (r.occupied){
    const sample = { name:'ผู้เข้าพัก', isStaffLinked: false, avatarUrl: null };
    content = \`<div class="hc-title">ห้อง \${r.no} • มีคนพัก</div><div class="hc-list">
      <div class="person"><div class="avatar">\${renderAvatar(sample, 0)}</div><div class="person-name">\${sample.name}</div><span class="role-badge">ผู้พัก</span></div>
    </div>\`;
  } else {
    let extraTitle = r.isDutyRoom ? ' (ห้องพักเวร)' : (r.isFamilyRoom && ${buildingId} === 4 ? ' (ห้องครอบครัวพิเศษ)' : '');
    const vAction = r.isTraineeMode ? "✓ ห้องว่าง (คลิกจัดนักศึกษาเข้าพัก)" : "✓ ห้องว่าง (คลิกเพื่อจัดคนเข้าห้อง)";
    content = \`<div class="hc-title" style="background:#f1f5f9;color:#475569">ห้อง \${r.no}\${extraTitle} • มีเตียงว่าง</div><div class="vacant-badge">\${vAction}</div>\`;
  }

  card.innerHTML = content;
  card.style.transform = \`translate3d(\${x}px,\${y}px,\${z + D/2}px) rotateY(\${-ry}deg) rotateX(\${-rx}deg) translateZ(180px) translate(-50%, \${yShift})\`;
  card.classList.add('show');
}

function ghostFront(f, me){
  const list = floorRooms[f] || [];
  list.forEach(o => o.el.classList.remove('ghost'));
  if (!me) return;
  const s = Math.sin(ry*Math.PI/180), c = Math.cos(ry*Math.PI/180);
  const depth = o => -o.x*s + o.z*c;
  const sx = o => o.x*c + o.z*s;
  list.forEach(o => { if (depth(o) - depth(me) > 40 && Math.abs(sx(o) - sx(me)) < 95) o.el.classList.add('ghost'); });
}

let isExploded = false;
function explode(on){
  clearTimeout(collapseT);
  const apply = v => {
    isExploded = v;
    if (bld) bld.classList.toggle('exploded', v);
    floorEls.forEach((el, f) => el.style.setProperty('--lift', v ? f*EX + 'px' : '0px'));
    if (roofGrp) roofGrp.style.setProperty('--lift', v ? (floorEls.length * EX + 16) + 'px' : '0px');
    updateHoverCard();
  };
  if (on) apply(true); else collapseT = setTimeout(() => apply(false), 500);
}

function box(cls, w, h, d, x, y, z, front, side, interior){
  const el = document.createElement('div');
  el.className = 'box ' + cls;
  el.style.cssText = \`--w:\${w}px;--h:\${h}px;--d:\${d}px;transform:translate3d(\${x}px,\${y}px,\${z}px)\`;
  const hits = cls.indexOf('room') !== -1 ? '<div class="hitbox front"></div><div class="hitbox top"></div>' : '';
  el.innerHTML = \`\${hits}<div class="face f">\${front||''}</div><div class="face b"></div><div class="face r">\${side||''}</div><div class="face l">\${side||''}</div><div class="face t"></div><div class="face bt"></div>\${interior||''}\`;
  return el;
}

function createPitchedRoof(w, d, yRoof){
  const grp = document.createElement('div');
  grp.className = 'box';
  grp.appendChild(box('roof', w, 12, d, 0, yRoof, 0));

  const ridgeH = 48;
  const halfD = d / 2;
  const slopeLen = Math.hypot(halfD, ridgeH);
  const angle = Math.atan2(ridgeH, halfD) * 180 / Math.PI;

  const frontSlope = document.createElement('div');
  frontSlope.className = 'roof-panel';
  frontSlope.style.cssText = \`width:\${w}px;height:\${slopeLen}px;margin:\${-slopeLen/2}px 0 0 \${-w/2}px;transform:translate3d(0,\${yRoof - 6 - ridgeH/2}px,\${halfD/2}px) rotateX(\${90 - angle}deg)\`;
  grp.appendChild(frontSlope);

  const backSlope = document.createElement('div');
  backSlope.className = 'roof-panel';
  backSlope.style.cssText = \`width:\${w}px;height:\${slopeLen}px;margin:\${-slopeLen/2}px 0 0 \${-w/2}px;transform:translate3d(0,\${yRoof - 6 - ridgeH/2}px,\${-halfD/2}px) rotateY(180deg) rotateX(\${90 - angle}deg)\`;
  grp.appendChild(backSlope);

  [-w/2 + 2, w/2 - 2].forEach((xPos, idx) => {
    const gable = document.createElement('div');
    gable.className = 'gable';
    gable.style.cssText = \`border-width:0 \${halfD}px \${ridgeH}px \${halfD}px;border-color:transparent transparent var(--roof) transparent;margin:\${-ridgeH}px 0 0 \${-halfD}px;transform:translate3d(\${xPos}px,\${yRoof - 6}px,0) rotateY(\${idx === 0 ? -90 : 90}deg)\`;
    grp.appendChild(gable);
  });

  return grp;
}

function showPanel(r, f){
  $('panel').hidden = false;
  const pav = $('pavatars');
  const btn = $('paction');
  const dutyLabel = r.isDutyRoom ? ' • <span style="color:#0284c7;font-weight:800">ห้องพักเวร</span>' : '';
  const famLabel = (r.isFamilyRoom && ${buildingId} === 4) ? ' • <span style="color:#d97706;font-weight:800">ห้องครอบครัวพิเศษ</span>' : '';

  if (r.isTraineeMode && r.hasStaffResident) {
    pav.innerHTML = (r.tenants && r.tenants.length ? r.tenants : [{ name: 'เจ้าหน้าที่', role: 'บุคลากร' }]).map((t, i) => \`<div class="avatar" title="\${t.name} (เจ้าหน้าที่)">\${renderAvatar(t, i)}</div>\`).join('');
    const names = (r.tenants && r.tenants.length ? r.tenants : [{ name: 'เจ้าหน้าที่' }]).map(t => t.name).join(', ');
    $('ptext').innerHTML = \`<b>ห้อง \${r.no}</b> • ชั้น \${f+1}\${dutyLabel || famLabel} • <span style="color:#64748b;font-weight:700">🔒 มีเจ้าหน้าที่พักอยู่ (ดูอย่างเดียว)</span> <small style="color:var(--muted)">(\${names})</small>\`;
    btn.textContent = '🔒 ดูรายละเอียดห้องพัก';
    btn.className = 'p-btn p-btn-manage';
    btn.style.background = '#334155';
  } else if (r.occupied && r.tenants && r.tenants.length){
    btn.style.background = '';
    pav.innerHTML = r.tenants.map((t, i) => \`<div class="avatar" title="\${t.name} (\${t.isFamily ? 'สมาชิกครอบครัว' : 'ผู้พักหลัก'})">\${renderAvatar(t, i)}</div>\`).join('');
    const names = r.tenants.map(t => \`\${t.name}\${t.isFamily ? ' [ครอบครัว]' : ''}\`).join(', ');
    const isOver = r.tenants.length > r.capacity;
    const overText = isOver ? \` • <span class="p-warn">⚠️ เกินเตียง (\${r.tenants.length}/\${r.capacity} เตียง - ต้องเพิ่มเตียง)</span>\` : \` • พัก \${r.tenants.length}/\${r.capacity} คน\`;
    const isFull = r.tenants.length >= r.capacity;
    const statusText = isFull
      ? '<span style="color:#15803d;font-weight:700">เต็ม</span>'
      : '<span style="color:#d97706;font-weight:700">มีเตียงว่าง</span>';
    $('ptext').innerHTML = \`<b>ห้อง \${r.no}</b> • ชั้น \${f+1}\${dutyLabel || famLabel} • \${statusText}\${overText} <small style="color:var(--muted)">(\${names})</small>\`;
    btn.textContent = isOver ? '⚠️ เพิ่มเตียง / จัดการห้อง' : '⚙️ จัดการห้องนี้';
    btn.className = 'p-btn p-btn-manage';
  } else {
    pav.innerHTML = '';
    $('ptext').innerHTML = \`<b>ห้อง \${r.no}</b> • ชั้น \${f+1}\${dutyLabel || famLabel} • <span style="color:#059669;font-weight:700">มีเตียงว่าง (ห้องว่าง)</span>\`;
    btn.textContent = '➕ จัดคนเข้าห้องนี้';
    btn.className = 'p-btn p-btn-checkin';
  }
}

const femaleWalkerSVG = '<svg viewBox="0 0 24 36"><path d="M6 10 C6 20, 18 20, 18 10 Z" fill="#7c2d12"/><circle cx="12" cy="7" r="4.5" fill="#fed7aa"/><path d="M7 6 C7 2, 17 2, 17 6 Z" fill="#7c2d12"/><circle cx="12" cy="3" r="1.5" fill="#f43f5e"/><path d="M8 12 L16 12 L18 22 L6 22 Z" fill="#fb7185"/><path d="M6 22 L18 22 L20 28 L4 28 Z" fill="#1e3a8a"/><rect x="8" y="28" width="2.5" height="7" rx="1" fill="#fed7aa"/><rect x="13.5" y="28" width="2.5" height="7" rx="1" fill="#fed7aa"/><rect x="7.5" y="33" width="3" height="2.5" rx="1" fill="#be185d"/><rect x="13" y="33" width="3" height="2.5" rx="1" fill="#be185d"/></svg>';
const maleWalkerSVG = '<svg viewBox="0 0 24 36"><circle cx="12" cy="7" r="4.5" fill="#fed7aa"/><path d="M7 6 C7 2, 17 2, 17 6 Z" fill="#1e293b"/><rect x="7" y="12" width="10" height="11" rx="2" fill="#0284c7"/><rect x="7" y="23" width="4" height="10" rx="1" fill="#1e293b"/><rect x="13" y="23" width="4" height="10" rx="1" fill="#1e293b"/></svg>';
const femaleActivitySVG = '<svg viewBox="0 0 24 32"><path d="M6 10 C6 20, 18 20, 18 10 Z" fill="#7c2d12"/><circle cx="12" cy="7" r="4.5" fill="#fed7aa"/><path d="M7 6 C7 2, 17 2, 17 6 Z" fill="#7c2d12"/><circle cx="12" cy="3" r="1.5" fill="#f43f5e"/><path d="M8 12 L16 12 L18 22 L6 22 Z" fill="#fb7185"/><rect x="7" y="22" width="10" height="7" rx="2" fill="#1e3a8a"/></svg>';
const maleActivitySVG = '<svg viewBox="0 0 24 32"><circle cx="12" cy="7" r="4.5" fill="#fed7aa"/><path d="M7 6 C7 2, 17 2, 17 6 Z" fill="#1e293b"/><rect x="7" y="12" width="10" height="10" rx="2" fill="#0284c7"/><rect x="7" y="22" width="10" height="7" rx="2" fill="#1e293b"/></svg>';
const femaleSleeperSVG = '<svg viewBox="0 0 24 16"><circle cx="20" cy="8" r="4" fill="#fed7aa"/><path d="M15 8 C15 3, 24 3, 24 8 Z" fill="#7c2d12"/><path d="M2 14 C2 7, 16 7, 16 14 Z" fill="#f43f5e"/></svg>';
const maleSleeperSVG = '<svg viewBox="0 0 24 16"><circle cx="20" cy="8" r="4" fill="#fed7aa"/><path d="M16 6 C16 3, 23 3, 23 6 Z" fill="#1e293b"/><path d="M2 14 C2 7, 16 7, 16 14 Z" fill="#0284c7"/></svg>';
const tvHTML = '<div class="p-tv"><div class="p-tv-screen"></div></div>';

const ROOM_THEMES = [
  {
    themeName: 'มุมทำงาน & อ่านหนังสือ',
    icon: '💻',
    act1Name: 'อ่านหนังสือ & ทำงาน 💻',
    act1Icon: '💻',
    act2Name: 'เล่นกีตาร์โปร่ง 🎸',
    act2Icon: '🎸',
    rugGrad: 'radial-gradient(circle, #38bdf8 0%, #0284c7 100%)',
    wallColor: 'linear-gradient(180deg, #e0f2fe, #bae6fd)',
    decor1: { emoji: '🪴', name: 'ต้นมอนสเตอร่า', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🎸', name: 'กีตาร์โปร่ง', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '💻', name: 'โต๊ะทำงาน & โน้ตบุ๊ก', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '📚 ตู้หนังสือ',
    tvScreen: 'SMART TV'
  },
  {
    themeName: 'สายเกมมิ่ง & อีสปอร์ต',
    icon: '🎮',
    act1Name: 'เล่นเกมคอนโซล 🎮',
    act1Icon: '🎮',
    act2Name: 'สวมหูฟัง RGB 🎧',
    act2Icon: '🎧',
    rugGrad: 'repeating-linear-gradient(45deg, #a855f7 0 5px, #7c3aed 5px 10px)',
    wallColor: 'linear-gradient(180deg, #f3e8ff, #e9d5ff)',
    decor1: { emoji: '🧸', name: 'ตุ๊กตาหมี', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🎧', name: 'หูฟังเกมมิ่ง', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🎮', name: 'จอยเกม & คอนโซล', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🕹️ RGB SETUP',
    tvScreen: 'GAME ON'
  },
  {
    themeName: 'คาเฟ่อบอุ่น & จิบกาแฟ',
    icon: '☕',
    act1Name: 'จิบกาแฟพักผ่อน ☕',
    act1Icon: '☕',
    act2Name: 'อ่านหนังสือใต้โคมไฟ 💡',
    act2Icon: '💡',
    rugGrad: 'radial-gradient(circle, #fde68a 0%, #d97706 100%)',
    wallColor: 'linear-gradient(180deg, #fefce8, #fef3c7)',
    decor1: { emoji: '🌿', name: 'ต้นยางอินเดีย', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '💡', name: 'โคมไฟตั้งพื้น', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '☕', name: 'โต๊ะกาแฟ & โซฟา', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '☕ COZY CAFE',
    tvScreen: 'CHILL L-FI'
  },
  {
    themeName: 'รักสุขภาพ & ฟิตเนส',
    icon: '🧘',
    act1Name: 'เล่นโยคะฟิตเนส 🧘',
    act1Icon: '🧘',
    act2Name: 'ยกดัมเบลออกกำลัง 🏋️',
    act2Icon: '🏋️',
    rugGrad: 'radial-gradient(circle, #a7f3d0 0%, #059669 100%)',
    wallColor: 'linear-gradient(180deg, #ecfdf5, #d1fae5)',
    decor1: { emoji: '🌵', name: 'แคคตัส', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🏋️', name: 'ดัมเบลคู่', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🧘', name: 'เสื่อโยคะ', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🥗 HEALTHY',
    tvScreen: 'YOGA CLASS'
  },
  {
    themeName: 'ดนตรี & ศิลปะ',
    icon: '🎨',
    act1Name: 'เล่นเปียโนไฟฟ้า 🎹',
    act1Icon: '🎹',
    act2Name: 'วาดรูปบนขาตั้ง 🎨',
    act2Icon: '🎨',
    rugGrad: 'repeating-linear-gradient(135deg, #f472b6 0 6px, #ec4899 6px 12px)',
    wallColor: 'linear-gradient(180deg, #fdf2f8, #fce7f3)',
    decor1: { emoji: '🎹', name: 'เปียโนไฟฟ้า', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🎨', name: 'ขาตั้งภาพวาด', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🪑', name: 'เก้าอี้พักผ่อน', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🎼 STUDIO',
    tvScreen: 'MUSIC TV'
  },
  {
    themeName: 'ธรรมชาติ & ชงชาญี่ปุ่น',
    icon: '🍵',
    act1Name: 'ชงชาญี่ปุ่น 🍵',
    act1Icon: '🍵',
    act2Name: 'จัดแจกันไผ่กวนอิม 🎋',
    act2Icon: '🎋',
    rugGrad: 'repeating-linear-gradient(90deg, #d4d4d8 0 4px, #a1a1aa 4px 8px)',
    wallColor: 'linear-gradient(180deg, #f4f4f5, #e4e4e7)',
    decor1: { emoji: '🎋', name: 'ไผ่กวนอิม', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🪞', name: 'กระจกแต่งตัว', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🍵', name: 'โต๊ะชงชาญี่ปุ่น', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🎍 ZEN GARDEN',
    tvScreen: 'NATURE 4K'
  },
  {
    themeName: 'มินิมอล & พักผ่อนสบาย',
    icon: '🛋️',
    act1Name: 'ฟังวิทยุเรโทร 📻',
    act1Icon: '📻',
    act2Name: 'พักผ่อนบนโซฟานุ่ม 🛋️',
    act2Icon: '🛋️',
    rugGrad: 'radial-gradient(circle, #fed7aa 0%, #ea580c 100%)',
    wallColor: 'linear-gradient(180deg, #fff7ed, #ffedd5)',
    decor1: { emoji: '📻', name: 'วิทยุเรโทร', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🪴', name: 'กระถางแคคตัส', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🛋️', name: 'โซฟามินิมอล', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🛋️ LIVING',
    tvScreen: 'MOVIE HD'
  },
  {
    themeName: 'สายไอที & แกดเจ็ต',
    icon: '🖥️',
    act1Name: 'เขียนโค้ดคอมพิวเตอร์ 💻',
    act1Icon: '💻',
    act2Name: 'คุมเครื่องพิมพ์ 3D 🖨️',
    act2Icon: '🖨️',
    rugGrad: 'repeating-linear-gradient(45deg, #60a5fa 0 6px, #2563eb 6px 12px)',
    wallColor: 'linear-gradient(180deg, #eff6ff, #dbeafe)',
    decor1: { emoji: '🖨️', name: 'เครื่องพิมพ์ 3D', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🤖', name: 'หุ่นยนต์ฟิกเกอร์', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🖥️', name: 'สถานีคอมพิวเตอร์', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '⚡ TECH LAB',
    tvScreen: 'CODING...'
  }
];

function getRoomTheme(roomNo){
  const seed = parseInt(String(roomNo || '101').replace(/[^0-9]/g, ''), 10) || 101;
  const idx = (seed * 5 + 3) % ROOM_THEMES.length;
  return ROOM_THEMES[idx];
}

function makeWinHTML(r){
  const occCount = (r.tenants && r.tenants.length) || (r.occupied ? 1 : 0);
  const p1Lit = occCount >= 1;
  const p2Lit = occCount >= 2;

  const p1Cls = p1Lit ? 'pane lit' : 'pane dark';
  const p2Cls = p2Lit ? 'pane lit' : 'pane dark';

  const roomSeed = parseInt(String(r.no || '101').replace(/[^0-9]/g, ''), 10) || 101;
  const act1 = roomSeed % 3; // 0: walking, 1: sleeping, 2: activity 1
  const act2 = (roomSeed * 7 + 2) % 3; // 0: sleeping, 1: walking, 2: activity 2
  const theme = getRoomTheme(r.no);

  const t1 = (r.tenants && r.tenants[0]) || null;
  const t2 = (r.tenants && r.tenants[1]) || null;
  const g1 = (t1 && t1.gender) || (${buildingId} === 1 ? 'F' : (${buildingId} === 4 ? 'M' : 'F'));
  const g2 = (t2 && t2.gender) || (${buildingId} === 1 ? 'F' : (${buildingId} === 4 ? 'M' : 'F'));

  let p1Char = '';
  if (p1Lit) {
    if (act1 === 0) p1Char = '<div class="p-walker">' + (g1 === 'F' ? femaleWalkerSVG : maleWalkerSVG) + '</div>';
    else if (act1 === 1) p1Char = '<div class="p-sleeper l">' + (g1 === 'F' ? femaleSleeperSVG : maleSleeperSVG) + '<span class="p-zzz" style="top:-6px;right:0">z</span></div>';
    else p1Char = '<div class="p-watcher">' + (g1 === 'F' ? femaleActivitySVG : maleActivitySVG) + '<span class="p-act-icon">' + theme.act1Icon + '</span></div>';
  }

  let p2Char = '';
  if (p2Lit) {
    if (act2 === 0) p2Char = '<div class="p-sleeper r">' + (g2 === 'F' ? femaleSleeperSVG : maleSleeperSVG) + '<span class="p-zzz" style="top:-6px;right:0">z</span></div>';
    else if (act2 === 1) p2Char = '<div class="p-walker" style="animation-delay:-2.7s;">' + (g2 === 'F' ? femaleWalkerSVG : maleWalkerSVG) + '</div>';
    else p2Char = '<div class="p-watcher">' + (g2 === 'F' ? femaleActivitySVG : maleActivitySVG) + '<span class="p-act-icon">' + theme.act2Icon + '</span></div>';
  }

  const pane1Content = '<div class="p-floor"></div><div class="p-bed l"></div>' + tvHTML + p1Char;
  const pane2Content = '<div class="p-floor"></div><div class="p-bed r"></div>' + p2Char;

  return '<div class="win"><div class="' + p1Cls + '">' + pane1Content + '</div><div class="' + p2Cls + '">' + pane2Content + '</div></div>';
}

function makeSideWinsHTML(r){
  const w = makeWinHTML(r);
  return '<div class="side-wins">' + w + w + '</div>';
}

function makeRoomInteriorHTML(r){
  const occCount = (r.tenants && r.tenants.length) || (r.occupied ? 1 : 0);
  const p1Lit = occCount >= 1;
  const p2Lit = occCount >= 2;

  const roomSeed = parseInt(String(r.no || '101').replace(/[^0-9]/g, ''), 10) || 101;
  const act1 = roomSeed % 3;
  const act2 = (roomSeed * 7 + 2) % 3;
  const theme = getRoomTheme(r.no);

  const t1 = (r.tenants && r.tenants[0]) || null;
  const t2 = (r.tenants && r.tenants[1]) || null;
  const g1 = (t1 && t1.gender) || (${buildingId} === 1 ? 'F' : (${buildingId} === 4 ? 'M' : 'F'));
  const g2 = (t2 && t2.gender) || (${buildingId} === 1 ? 'F' : (${buildingId} === 4 ? 'M' : 'F'));

  let p1Char3D = '';
  if (p1Lit) {
    if (act1 === 0) {
      p1Char3D = '<div class="char-3d char-walk">' + (g1 === 'F' ? femaleWalkerSVG : maleWalkerSVG) + '</div>';
    } else if (act1 === 1) {
      p1Char3D = '<div class="char-bed l">' + (g1 === 'F' ? femaleSleeperSVG : maleSleeperSVG) + '<span class="p-zzz" style="top:-8px;right:0">z</span></div>';
    } else {
      p1Char3D = '<div class="char-3d char-act l">' + (g1 === 'F' ? femaleActivitySVG : maleActivitySVG) + '<span class="char-act-badge">' + theme.act1Icon + '</span><span class="char-act-label">' + theme.act1Name + '</span></div>';
    }
  }

  let p2Char3D = '';
  if (p2Lit) {
    if (act2 === 0) {
      p2Char3D = '<div class="char-bed r">' + (g2 === 'F' ? femaleSleeperSVG : maleSleeperSVG) + '<span class="p-zzz" style="top:-8px;right:0">z</span></div>';
    } else if (act2 === 1) {
      p2Char3D = '<div class="char-3d char-walk r">' + (g2 === 'F' ? femaleWalkerSVG : maleWalkerSVG) + '</div>';
    } else {
      p2Char3D = '<div class="char-3d char-act r">' + (g2 === 'F' ? femaleActivitySVG : maleActivitySVG) + '<span class="char-act-badge">' + theme.act2Icon + '</span><span class="char-act-label">' + theme.act2Name + '</span></div>';
    }
  }

  const tvLitClass = occCount > 0 ? 'lit' : 'dark';

  return '<div class="interior">' +
    '<div class="in-back" style="background:' + theme.wallColor + '">' +
      '<div class="b-tv ' + tvLitClass + '">' + theme.tvScreen + '</div>' +
      '<div class="in-title">' + r.no + ' • ' + theme.themeName + '</div>' +
    '</div>' +
    '<div class="in-left"></div>' +
    '<div class="in-right"></div>' +
    '<div class="in-floor">' +
      '<div class="f-bed l ' + (p1Lit ? 'occ' : 'vac') + '"><div class="pillow"></div><div class="blanket"></div></div>' +
      '<div class="f-bed r ' + (p2Lit ? 'occ' : 'vac') + '"><div class="pillow"></div><div class="blanket"></div></div>' +
      '<div class="f-rug" style="background:' + theme.rugGrad + ';"></div>' +
      '<div class="f-item" style="' + theme.decor1.pos + '" title="' + theme.decor1.name + '">' + theme.decor1.emoji + '</div>' +
      '<div class="f-item" style="' + theme.decor2.pos + '" title="' + theme.decor2.name + '">' + theme.decor2.emoji + '</div>' +
      '<div class="f-item" style="' + theme.centerDecor.pos + '" title="' + theme.centerDecor.name + '">' + theme.centerDecor.emoji + '</div>' +
    '</div>' +
    p1Char3D + p2Char3D +
    '<div class="in-rail"></div>' +
  '</div>';
}

let isCutaway = false;
function toggleDollhouse(){
  isCutaway = !isCutaway;
  const b = $('building');
  if (b) b.classList.toggle('bld-cutaway', isCutaway);
  const btn = $('btnDollhouse');
  if (btn) {
    btn.innerHTML = isCutaway ? '🏢 ปิดผนังตึก 3D' : '🏠 เปิดห้องดูข้างใน 3D';
    btn.classList.toggle('active', isCutaway);
  }
}

function render(){
  const n = DATA.length, cols = 4, rows = 1;
  const bw = cols*(W+GAP) - GAP, bd = rows*(D+GAP) - GAP, step = H + SL;
  const yOf = f => -(f - (n-1)/2) * step;
  const gx = c => (c - (cols-1)/2) * (W+GAP), gz = r => (r - (rows-1)/2) * (D+GAP);

  const hc = $('hoverCard');
  bld.innerHTML = '';
  if (hc) bld.appendChild(hc);
  floorEls = []; floorRooms = [];

  const yBase = yOf(0) + H/2 + SL;
  geo = {yTop: yOf(0) + H/2, yG: yBase + 14, hd: bd/2, hw: W + GAP};
  const g = document.createElement('div');
  g.className = 'ground';
  g.style.transform = \`translate3d(0,\${yBase + 14}px,0) rotateX(90deg)\`;
  bld.appendChild(g);
  bld.appendChild(box('plinth', bw+40, 14, bd+40, 0, yBase + 7, 0));

  DATA.forEach((list, f) => {
    const fl = document.createElement('div');
    fl.className = 'floor';
    fl.style.setProperty('--y', yOf(f) + 'px'); floorEls[f] = fl;
    const ground = f === 0;
    const groundNote = (${buildingId} === 3 || ${buildingId} === 4) ? ' (ใต้ถุนโล่งด้านซ้าย/ที่จอดรถ)' : ' (ใต้ถุนโล่งตรงกลาง/ที่จอดรถ)';
    fl.addEventListener('mouseleave', () => explode(false));
    fl.addEventListener('mouseenter', () => {
      explode(true);
      const c = list.filter(r => r.occupied).length;
      $('info').innerHTML = \`ชั้น \${f+1}\${ground ? groundNote : ''} — มีคนพัก <b>\${c}/\${list.length}</b> ห้อง (ว่าง \${list.length-c})\`;
    });
    fl.appendChild(box('slab', bw+24, SL, bd+24, 0, H/2 + SL/2, 0, \`<span class="tag">ชั้น \${f+1}</span>\`));

    if (ground) {
      if (${buildingId} === 3 || ${buildingId} === 4) {
        // ตึก 3, ตึก 4: ด้านซ้ายไม่มีห้อง มีเสาค้ำใต้ถุนโล่งที่ช่อง 0 และ 1
        [gx(0), gx(1)].forEach(x => [-(bd/2-10), 0, bd/2-10].forEach(z => fl.appendChild(box('pillar', 12, H, 12, x, 0, z))));
      } else {
        // ตึก 1, 2: ใต้ถุนโล่งตรงกลาง
        [-W, 0, W].forEach(x => [-(bd/2-10), 0, bd/2-10].forEach(z => fl.appendChild(box('pillar', 12, H, 12, x, 0, z))));
      }
    }

    list.forEach((r, i) => {
      let c = i;
      if (ground) {
        if (${buildingId} === 3 || ${buildingId} === 4) {
          // ตึก 3, ตึก 4: ด้านซ้ายไม่มี มีด้านขวาสองห้อง (311/411 อยู่ช่อง 2, 312/412 อยู่ช่อง 3)
          c = i === 0 ? 2 : 3;
        } else {
          c = i === 0 ? 0 : 3;
        }
      }
      const rw = 0;
      const x = gx(c), z = gz(rw);
      const subtagHtml = r.isDutyRoom
        ? '<div class="room-subtag duty">พักเวร</div>'
        : (r.isFamilyRoom ? '<div class="room-subtag fam">ครอบครัว</div>' : '');
      const winHtml = makeWinHTML(r);
      const sideWinsHtml = makeSideWinsHTML(r);
      const interiorHtml = makeRoomInteriorHTML(r);
      const el = box('room' + (r.isMaintenance ? ' maint' : (r.occupied ? ' occ' : '')) + (r.isFamilyRoom ? ' fam-room' : ''), W, H, D, x, 0, z,
        \`\${winHtml}<div class="no">\${r.no}</div>\${subtagHtml}\`,
        sideWinsHtml,
        interiorHtml);
      const me = {el, x, z};
      (floorRooms[f] = floorRooms[f] || []).push(me);
      el.addEventListener('mouseenter', () => {
        clearTimeout(hovTimer);
        setHov(el, {r, f, x, z});
        ghostFront(f, me);
      });
      el.addEventListener('mouseleave', () => {
        clearTimeout(hovTimer);
        hovTimer = setTimeout(() => {
          if (hovEl === el) {
            setHov(null, null);
            ghostFront(f, null);
          }
        }, 120);
      });
      el.addEventListener('click', () => {
        if (selEl) { selEl.classList.remove('sel'); selEl.classList.remove('open'); }
        selEl = el; selR = r; selF = f;
        el.classList.add('sel');
        el.classList.add('open');
        showPanel(r, f);
        onRoomClick(r, f);
      });
      fl.appendChild(el);
    });
    bld.appendChild(fl);
  });

  const yRoof = yOf(n-1) - H/2 - 6;
  roofGrp = document.createElement('div');
  roofGrp.className = 'floor roofgrp';
  roofGrp.style.setProperty('--y', '0px');
  roofGrp.appendChild(createPitchedRoof(bw+34, bd+34, yRoof));
  bld.appendChild(roofGrp);
}

$('paction').onclick = () => {
  if (!selR) return;
  onRoomClick(selR, selF);
};

let ry = -35, rx = -22, drag = null;
const stage = $('stage');
const setRot = () => {
  bld.style.transform = \`rotateX(\${rx}deg) rotateY(\${ry}deg)\`;
  updateHoverCard();
};
stage.addEventListener('pointerdown', e => { drag = {x:e.clientX, y:e.clientY, ry, rx}; stage.style.cursor='grabbing'; });
addEventListener('pointerup', () => { drag = null; stage.style.cursor='grab'; });
addEventListener('pointermove', e => {
  if (!drag) return;
  ry = drag.ry + (e.clientX - drag.x) * .4;
  rx = Math.max(-80, Math.min(10, drag.rx - (e.clientY - drag.y) * .3));
  setRot();
});

render();

${
  showCats
    ? `
// แมวจำลองเฉพาะตึกที่มีกำหนดให้มีแมว
const PERSIAN = \`<svg viewBox="0 0 120 90"><ellipse cx="58" cy="86" rx="38" ry="4" fill="rgba(0,0,0,.2)"/>
<path class="tail" d="M30 58C6 58 0 32 14 20C22 13 32 18 27 28C24 35 29 45 40 49Z" fill="#fff" stroke="#f1f5f9" stroke-width="2" stroke-linejoin="round"/>
<g class="hip leg1" style="transform-origin:46px 62px"><rect x="40" y="60" width="12" height="16" rx="6" fill="#fff"/><g class="knee" style="transform-origin:46px 74px"><rect x="41" y="72" width="10" height="14" rx="5" fill="#fff"/><ellipse cx="47" cy="85" rx="7" ry="3.6" fill="#fff"/></g></g>
<g class="hip leg2" style="transform-origin:86px 62px"><rect x="80" y="60" width="12" height="16" rx="6" fill="#fff"/><g class="knee" style="transform-origin:86px 74px"><rect x="81" y="72" width="10" height="14" rx="5" fill="#fff"/><ellipse cx="87" cy="85" rx="7" ry="3.6" fill="#fff"/></g></g>
<ellipse cx="58" cy="56" rx="35" ry="22" fill="#fff" stroke="#f1f5f9" stroke-width="2"/>
<g class="hip leg2" style="transform-origin:36px 62px"><rect x="30" y="60" width="12" height="16" rx="6" fill="#fff"/><g class="knee" style="transform-origin:36px 74px"><rect x="31" y="72" width="10" height="14" rx="5" fill="#fff"/><ellipse cx="37" cy="85" rx="7" ry="3.6" fill="#fff"/></g></g>
<g class="hip leg1" style="transform-origin:76px 62px"><rect x="70" y="60" width="12" height="16" rx="6" fill="#fff"/><g class="knee" style="transform-origin:76px 74px"><rect x="71" y="72" width="10" height="14" rx="5" fill="#fff"/><ellipse cx="77" cy="85" rx="7" ry="3.6" fill="#fff"/></g></g>
<circle cx="86" cy="58" r="13" fill="#fff"/>
<path d="M78 26L80 6L94 19Z M98 19L112 6L114 27Z" fill="#fff" stroke="#f1f5f9" stroke-width="2" stroke-linejoin="round"/>
<path d="M82 22L83 12L90 19Z M102 19L109 12L110 22Z" fill="#fbcfe8"/>
<circle cx="97" cy="40" r="21" fill="#fff" stroke="#f1f5f9" stroke-width="2"/>
<circle cx="84" cy="52" r="8" fill="#fff"/><circle cx="110" cy="52" r="8" fill="#fff"/>
<ellipse cx="86" cy="47" rx="5" ry="3" fill="#fecdd3" opacity=".8"/><ellipse cx="108" cy="47" rx="5" ry="3" fill="#fecdd3" opacity=".8"/>
<circle cx="90" cy="38" r="5" fill="#fb923c"/><circle cx="104" cy="38" r="5" fill="#fb923c"/>
<circle cx="90" cy="38" r="2.4" fill="#111"/><circle cx="104" cy="38" r="2.4" fill="#111"/>
<circle cx="91" cy="36.5" r="1" fill="#fff"/><circle cx="105" cy="36.5" r="1" fill="#fff"/>
<path d="M94 45Q97 49 100 45Z" fill="#f472b6"/>
<path d="M97 49Q94 53 91 51M97 49Q100 53 103 51" stroke="#94a3b8" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>\`;

const TABBY = \`<svg viewBox="0 0 120 90"><ellipse cx="58" cy="86" rx="34" ry="4" fill="rgba(0,0,0,.2)"/>
<g class="tail"><path d="M28 56C10 54 6 34 14 22" stroke="#6b7280" stroke-width="9" fill="none" stroke-linecap="round"/><path d="M28 56C10 54 6 34 14 22" stroke="#1f2937" stroke-width="9" fill="none" stroke-dasharray="4 8"/></g>
<g class="hip leg1" style="transform-origin:46px 62px"><rect x="40" y="60" width="12" height="16" rx="6" fill="#4b5563"/><g class="knee" style="transform-origin:46px 74px"><rect x="41" y="72" width="10" height="14" rx="5" fill="#4b5563"/><ellipse cx="47" cy="85" rx="7" ry="3.6" fill="#e5e7eb"/></g></g>
<g class="hip leg2" style="transform-origin:86px 62px"><rect x="80" y="60" width="12" height="16" rx="6" fill="#4b5563"/><g class="knee" style="transform-origin:86px 74px"><rect x="81" y="72" width="10" height="14" rx="5" fill="#4b5563"/><ellipse cx="87" cy="85" rx="7" ry="3.6" fill="#e5e7eb"/></g></g>
<ellipse cx="58" cy="58" rx="33" ry="18" fill="#6b7280"/>
<path d="M44 42q3 8 0 14M54 40q3 9 0 16M64 40q3 9 0 16M74 42q3 8 0 14" stroke="#1f2937" stroke-width="3.5" fill="none" stroke-linecap="round"/>
<g class="hip leg2" style="transform-origin:36px 62px"><rect x="30" y="60" width="12" height="16" rx="6" fill="#6b7280"/><g class="knee" style="transform-origin:36px 74px"><rect x="31" y="72" width="10" height="14" rx="5" fill="#6b7280"/><ellipse cx="37" cy="85" rx="7" ry="3.6" fill="#fff"/></g></g>
<g class="hip leg1" style="transform-origin:76px 62px"><rect x="70" y="60" width="12" height="16" rx="6" fill="#6b7280"/><g class="knee" style="transform-origin:76px 74px"><rect x="71" y="72" width="10" height="14" rx="5" fill="#6b7280"/><ellipse cx="77" cy="85" rx="7" ry="3.6" fill="#fff"/></g></g>
<ellipse cx="82" cy="64" rx="8" ry="11" fill="#fff"/>
<path d="M78 32L79 8L94 24Z M98 24L112 8L113 33Z" fill="#6b7280"/>
<path d="M82 27L82.5 14L90 24Z M102 24L109 14L109 28Z" fill="#f9a8d4"/>
<circle cx="96" cy="42" r="19" fill="#6b7280"/>
<path d="M91 26v7M96 24v8M101 26v7" stroke="#1f2937" stroke-width="2.5" stroke-linecap="round"/>
<ellipse cx="96" cy="50" rx="9" ry="6.5" fill="#fff"/>
<ellipse cx="89" cy="40" rx="4.5" ry="5" fill="#86efac"/><ellipse cx="103" cy="40" rx="4.5" ry="5" fill="#86efac"/>
<ellipse cx="89" cy="40" rx="1.6" ry="4" fill="#111"/><ellipse cx="103" cy="40" rx="1.6" ry="4" fill="#111"/>
<circle cx="90" cy="38" r="1.1" fill="#fff"/><circle cx="104" cy="38" r="1.1" fill="#fff"/>
<path d="M93.5 47Q96 50 98.5 47Z" fill="#f472b6"/>
<path d="M96 50Q93.5 54 91 52.5M96 50Q98.5 54 101 52.5" stroke="#6b7280" stroke-width="1.3" fill="none" stroke-linecap="round"/>
<path d="M82 46H74M82 49L75 52M110 46H118M110 49L117 52" stroke="#6b7280" stroke-width="1" stroke-linecap="round"/></svg>\`;

const NODES = {
  i1:[-75,-65], i2:[0,-65], i3:[75,-65],
  i4:[-75,  0], i5:[0,  0], i6:[75,  0],
  i7:[-75, 65], i8:[0, 65], i9:[75, 65],
  g1:[-55, 125], g2:[55, 125], g3:[0, 175],
  gb1:[-55,-125], gb2:[55,-125],
  o1:[170,235], o2:[-170,240], o3:[0,275], o4:[280,150], o5:[-280,145], o6:[120,360], o7:[-120,365],
  o8:[340,260], o9:[-340,265], o10:[290,-40], o11:[-290,-40], b1:[210,-220], b2:[-210,-220], b3:[0,-245]
};
const LINKS = {
  i1:['i2','i4','gb1'], i2:['i1','i3','i5'], i3:['i2','i6','gb2'],
  i4:['i1','i5','i7'],  i5:['i2','i4','i6','i8'], i6:['i3','i5','i9'],
  i7:['i4','i8','g1'],  i8:['i5','i7','i9','g1','g2'], i9:['i6','i8','g2'],
  g1:['i7','i8','g3','o2'], g2:['i8','i9','g3','o1'], g3:['g1','g2','o1','o2','o3'],
  gb1:['i1','b2','b3'], gb2:['i3','b1','b3'],
  o1:['g2','g3','o3','o4','o6','o8'], o2:['g1','g3','o3','o5','o7','o9'], o3:['g3','o1','o2','o6','o7'],
  o4:['o1','o8','o10'], o5:['o2','o9','o11'], o6:['o1','o3'], o7:['o2','o3'],
  o8:['o1','o4','o6'], o9:['o2','o5','o7'], o10:['o4','b1'], o11:['o5','b2'],
  b1:['o10','b3','gb2'], b2:['o11','b3','gb1'], b3:['b1','b2','gb1','gb2']
};
const pickNext = (node, prev) => { const o = LINKS[node].filter(n => n !== prev); const l = o.length ? o : LINKS[node]; return l[Math.floor(Math.random()*l.length)]; };

const cats = [];
function addCat(svg, s, speed, spd, start){
  const el = document.createElement('div');
  el.className = 'cat'; el.style.setProperty('--spd', spd); el.innerHTML = svg; if (svg === PERSIAN) el.classList.add('white');
  bld.appendChild(el);
  const [x, z] = NODES[start];
  cats.push({el, s, speed, node:start, prev:null, target:pickNext(start, null), x, z, vx:1, vz:0, flip:1, wait:0});
}
addCat(PERSIAN, .55, 42, '.38s', 'i5');
addCat(TABBY,   .40, 68, '.27s', 'i8');

let lastT = 0;
function tick(t){
  const dt = Math.min(.05, (t - lastT) / 1000); lastT = t;
  const cr = Math.cos(ry*Math.PI/180), sr = Math.sin(ry*Math.PI/180);
  cats.forEach(c => {
    if (c.wait > 0){ c.wait -= dt; c.el.classList.add('stop'); }
    else {
      c.el.classList.remove('stop');
      const [tx, tz] = NODES[c.target];
      const dx = tx - c.x, dz = tz - c.z, d = Math.hypot(dx, dz), step = c.speed * dt;
      if (d <= step){
        c.x = tx; c.z = tz; c.prev = c.node; c.node = c.target; c.target = pickNext(c.node, c.prev);
        if (Math.random() < .35) c.wait = .6 + Math.random() * 1.6;
      } else { c.vx = dx/d; c.vz = dz/d; c.x += c.vx * step; c.z += c.vz * step; }
    }
    const a = geo.hd + 14, b = geo.hd + 34;
    let y = geo.yG;
    if (Math.abs(c.x) < geo.hw){
      const az = Math.abs(c.z);
      if (az <= a) y = geo.yTop;
      else if (az < b) y = geo.yTop + (geo.yG - geo.yTop) * (az - a) / (b - a);
    }
    const sv = c.vx * cr + c.vz * sr;
    if (Math.abs(sv) > .15) c.flip = sv > 0 ? 1 : -1;
    c.el.style.transform = \`translate3d(\${c.x}px,\${y}px,\${c.z}px) rotateY(\${-ry}deg) rotateX(\${-rx}deg) scale(\${c.flip * c.s},\${c.s})\`;
  });
  requestAnimationFrame(tick);
}
requestAnimationFrame(tick);
`
    : (showDogAndBike
        ? `
// ตึก 4: มอเตอร์ไซค์สีดำและหมาตัวสีดำวิ่งไล่รอบตึก (วิ่งช้า ชัดเจน ออกห่างจากตัวตึก)
const MOTORCYCLE_SVG = \`<svg viewBox="0 0 135 85">
  <ellipse cx="66" cy="76" rx="55" ry="5.5" fill="rgba(0,0,0,0.5)"/>
  <polygon points="114,38 175,20 175,64" fill="rgba(254,240,138,0.35)" />
  <!-- Rear Wheel (Center 30, 58) -->
  <circle cx="30" cy="58" r="16.5" fill="#09090b" stroke="#1c1917" stroke-width="3"/>
  <g class="spin-wheel-r">
    <circle cx="30" cy="58" r="13" fill="#18181b" stroke="#71717a" stroke-width="1.8"/>
    <circle cx="30" cy="58" r="8.5" fill="#27272a" stroke="#a1a1aa" stroke-width="1"/>
    <line x1="30" y1="58" x2="30" y2="45" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="30" y1="58" x2="42.4" y2="54" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="30" y1="58" x2="37.6" y2="68.5" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="30" y1="58" x2="22.4" y2="68.5" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="30" y1="58" x2="17.6" y2="54" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <circle cx="30" cy="58" r="4.2" fill="#ef4444" stroke="#b91c1c" stroke-width="0.8"/>
    <circle cx="30" cy="58" r="1.5" fill="#ffffff"/>
  </g>
  <path d="M22 52 L26 50 L28 54 L24 56 Z" fill="#eab308"/>
  <!-- Front Wheel (Center 102, 58) -->
  <circle cx="102" cy="58" r="16.5" fill="#09090b" stroke="#1c1917" stroke-width="3"/>
  <g class="spin-wheel-f">
    <circle cx="102" cy="58" r="13" fill="#18181b" stroke="#71717a" stroke-width="1.8"/>
    <circle cx="102" cy="58" r="8.5" fill="#27272a" stroke="#a1a1aa" stroke-width="1"/>
    <line x1="102" y1="58" x2="102" y2="45" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="102" y1="58" x2="114.4" y2="54" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="102" y1="58" x2="109.6" y2="68.5" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="102" y1="58" x2="94.4" y2="68.5" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <line x1="102" y1="58" x2="89.6" y2="54" stroke="#e4e4e7" stroke-width="2.4" stroke-linecap="round"/>
    <circle cx="102" cy="58" r="4.2" fill="#ef4444" stroke="#b91c1c" stroke-width="0.8"/>
    <circle cx="102" cy="58" r="1.5" fill="#ffffff"/>
  </g>
  <path d="M94 52 L98 50 L100 54 L96 56 Z" fill="#eab308"/>
  <!-- Swingarm & Exhaust -->
  <line x1="30" y1="58" x2="54" y2="54" stroke="#52525b" stroke-width="4.5" stroke-linecap="round"/>
  <line x1="38" y1="56" x2="48" y2="45" stroke="#ef4444" stroke-width="3.5" stroke-linecap="round"/>
  <path d="M48 60 L20 62 L12 62" stroke="#94a3b8" stroke-width="5" stroke-linecap="round"/>
  <path d="M48 60 L20 62 L12 62" stroke="#e2e8f0" stroke-width="2" stroke-linecap="round"/>
  <circle cx="10" cy="62" r="3" fill="#1e293b"/>
  <circle class="exhaust-puff" cx="6" cy="62" r="4" fill="#cbd5e1"/>
  <circle class="exhaust-puff" cx="1" cy="60" r="5" fill="#e2e8f0" style="animation-delay: 0.45s"/>
  <!-- Engine -->
  <rect x="50" y="48" width="22" height="15" rx="3" fill="#18181b" stroke="#3f3f46" stroke-width="1.5"/>
  <circle cx="56" cy="56" r="3.5" fill="#27272a" stroke="#71717a" stroke-width="1"/>
  <line x1="62" y1="51" x2="70" y2="51" stroke="#71717a" stroke-width="1.5"/>
  <line x1="62" y1="55" x2="70" y2="55" stroke="#71717a" stroke-width="1.5"/>
  <line x1="62" y1="59" x2="70" y2="59" stroke="#71717a" stroke-width="1.5"/>
  <!-- Suspension Forks -->
  <line x1="94" y1="36" x2="102" y2="58" stroke="#cbd5e1" stroke-width="4.5" stroke-linecap="round"/>
  <line x1="95" y1="41" x2="102" y2="58" stroke="#64748b" stroke-width="2.5"/>
  <!-- Fairing Body -->
  <path d="M32 44 L46 42 L64 38 L88 40 L100 34 L110 40 L98 52 L74 52 L52 54 Z" fill="#09090b" stroke="#27272a" stroke-width="1.8" stroke-linejoin="round"/>
  <path d="M56 40 Q74 38 90 41" stroke="#06b6d4" stroke-width="2.5" fill="none" stroke-linecap="round"/>
  <path d="M62 38 Q76 32 86 39 L82 46 L62 44 Z" fill="#18181b" stroke="#3f3f46" stroke-width="1.2"/>
  <path d="M35 43 Q47 41 58 43 L54 48 L37 47 Z" fill="#27272a" stroke="#09090b" stroke-width="1.2"/>
  <path d="M98 33 L108 26 L116 34 L106 42 Z" fill="#09090b" stroke="#27272a" stroke-width="1.2"/>
  <path d="M106 26 L112 18 L117 26 Z" fill="rgba(6,182,212,0.45)" stroke="#06b6d4" stroke-width="1.2"/>
  <polygon points="110,34 116,38 112,42 107,38" fill="#fef08a" stroke="#facc15" stroke-width="1"/>
  <rect x="31" y="43" width="5" height="4" rx="1.5" fill="#ef4444" filter="drop-shadow(0 0 4px #ef4444)"/>
  <line x1="95" y1="33" x2="98" y2="26" stroke="#94a3b8" stroke-width="3" stroke-linecap="round"/>
  <circle cx="96" cy="24" r="2.5" fill="#38bdf8"/>
  <!-- Rider -->
  <path d="M54 46 L64 56 L74 56" stroke="#18181b" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  <circle cx="73" cy="56" r="3" fill="#09090b"/>
  <path d="M52 44 L64 24 L78 27 L70 46 Z" fill="#09090b" stroke="#27272a" stroke-width="1.5"/>
  <path d="M66 26 L74 42" stroke="#06b6d4" stroke-width="1.8" fill="none"/>
  <path d="M70 27 L88 33 L96 31" stroke="#18181b" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  <circle cx="96" cy="31" r="2.8" fill="#3f3f46"/>
  <ellipse cx="75" cy="15" rx="11" ry="12" fill="#09090b" stroke="#27272a" stroke-width="1.5"/>
  <path d="M65 15 Q64 10 70 8" stroke="#06b6d4" stroke-width="2" fill="none"/>
  <path d="M78 11 Q87 14 84 20 L75 19 Z" fill="#06b6d4" opacity="0.95" stroke="#38bdf8" stroke-width="1"/>
  <path d="M79 13 Q84 15 82 18" stroke="#ffffff" stroke-width="1.2" fill="none" opacity="0.8"/>
</svg>\`;

const RUNNING_DOG_SVG = \`<svg viewBox="0 0 120 85" class="dog-bounce">
  <ellipse cx="58" cy="76" rx="46" ry="5" fill="rgba(0,0,0,0.45)"/>
  <!-- Wagging Tail (ฝังเบ้าแน่นหนาในสะโพก 32, 36 ไม่ลอยแยก) -->
  <g class="dog-tail">
    <circle cx="32" cy="36" r="6" fill="#18181b"/>
    <path d="M32 36 C20 26 16 12 22 7 C23 16 28 25 34 34 Z" fill="#18181b" stroke="#27272a" stroke-width="1.2"/>
  </g>
  <!-- Far Back Leg (B2) -->
  <g class="dog-hip-b2">
    <path d="M34 38 L22 54 L14 68" stroke="#09090b" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <ellipse cx="13" cy="69" rx="4.5" ry="2.5" fill="#09090b"/>
  </g>
  <!-- Far Front Leg (F2) -->
  <g class="dog-hip-f2">
    <path d="M76 40 L82 54 L92 68" stroke="#09090b" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <ellipse cx="93" cy="69" rx="4" ry="2.5" fill="#09090b"/>
  </g>
  <!-- Main Body: Torso seamlessly enclosing hip and shoulder joints -->
  <ellipse cx="38" cy="40" rx="18" ry="16" fill="#18181b" stroke="#27272a" stroke-width="1.5"/>
  <ellipse cx="68" cy="40" rx="20" ry="17" fill="#18181b"/>
  <path d="M36 24 L70 23 L74 57 L38 56 Z" fill="#18181b"/>
  <path d="M30 30 Q54 22 76 26" stroke="#3f3f46" stroke-width="2.5" fill="none" stroke-linecap="round"/>
  <path d="M62 30 Q76 34 74 48 Q62 50 62 30 Z" fill="#27272a" opacity="0.9"/>
  <!-- Near Back Leg (B1) with solid circular thigh joint -->
  <g class="dog-hip-b1">
    <circle cx="32" cy="38" r="8" fill="#18181b"/>
    <path d="M32 38 L22 55 L16 70" stroke="#18181b" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <path d="M32 38 L22 55 L16 70" stroke="#27272a" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <ellipse cx="15" cy="71" rx="5" ry="2.8" fill="#09090b"/>
  </g>
  <!-- Near Front Leg (F1) with solid circular shoulder joint -->
  <g class="dog-hip-f1">
    <circle cx="74" cy="40" r="8" fill="#18181b"/>
    <path d="M74 40 L80 54 L88 70" stroke="#18181b" stroke-width="7.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <path d="M74 40 L80 54 L88 70" stroke="#27272a" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <ellipse cx="89" cy="71" rx="5" ry="2.8" fill="#09090b"/>
  </g>
  <!-- Collar & Tag -->
  <path d="M78 28 L84 39" stroke="#ef4444" stroke-width="6" stroke-linecap="round"/>
  <circle cx="83" cy="40" r="3.5" fill="#fbbf24" stroke="#b45309" stroke-width="1"/>
  <!-- Head & Neck -->
  <path d="M70 25 L88 18 L92 34 L76 38 Z" fill="#18181b"/>
  <circle cx="90" cy="24" r="15" fill="#18181b" stroke="#27272a" stroke-width="1.5"/>
  <path d="M82 16 Q72 11 66 18 Q74 27 82 20 Z" fill="#09090b" stroke="#27272a" stroke-width="1.2"/>
  <path d="M94 22 Q106 24 108 30 Q100 36 92 33 Z" fill="#18181b" stroke="#27272a" stroke-width="1"/>
  <ellipse cx="106.5" cy="28" rx="3.5" ry="2.6" fill="#09090b"/>
  <circle cx="107" cy="27" r="0.9" fill="#ffffff"/>
  <path d="M98 30 Q104 36 100 39 Q95 36 97 31 Z" fill="#f43f5e" stroke="#e11d48" stroke-width="0.8"/>
  <polygon points="99,30 101,30 100,32.5" fill="#ffffff"/>
  <ellipse cx="93" cy="19" rx="3.5" ry="4" fill="#ffffff"/>
  <ellipse cx="94" cy="19" rx="2.4" ry="2.8" fill="#09090b"/>
  <circle cx="95" cy="18" r="1.1" fill="#ffffff"/>
  <path d="M90 15 Q94 14 97 16" stroke="#52525b" stroke-width="1.2" fill="none"/>
</svg>\`;

const motoEl = document.createElement('div');
motoEl.className = 'moto-runner';
motoEl.innerHTML = MOTORCYCLE_SVG;
bld.appendChild(motoEl);

const dogEl = document.createElement('div');
dogEl.className = 'dog-chaser';
dogEl.innerHTML = RUNNING_DOG_SVG + '<div class="dog-bark">โฮ่ง! 🐾</div>';
bld.appendChild(dogEl);

// ขยายระยะวิ่งให้ห่างออกจากตัวตึก (RX = 345, RZ = 195)
const RX = 345, RZ = 195;
const TRACK_LEN = 2 * Math.PI * Math.sqrt((RX * RX + RZ * RZ) / 2);
let bikeDist = 0;
// วิ่งช้าๆ สบายๆ เพื่อให้มองทันและเห็นชัดเจน (78 px/sec)
const BIKE_SPEED = 78;
const DOG_GAP = 68;

function getTrackPoint(dist) {
  const u = (dist / TRACK_LEN) * 2 * Math.PI;
  const x = RX * Math.sin(u);
  const z = RZ * Math.cos(u);
  const vx = RX * Math.cos(u);
  const vz = -RZ * Math.sin(u);
  return { x, z, vx, vz };
}

let lastBikeT = 0;
function tickBikeDog(t) {
  if (!lastBikeT) lastBikeT = t;
  const dt = Math.min(0.05, (t - lastBikeT) / 1000);
  lastBikeT = t;

  bikeDist = (bikeDist + BIKE_SPEED * dt) % TRACK_LEN;
  const dogDist = (bikeDist - DOG_GAP + TRACK_LEN) % TRACK_LEN;

  const bPt = getTrackPoint(bikeDist);
  const dPt = getTrackPoint(dogDist);

  const cr = Math.cos(ry * Math.PI / 180), sr = Math.sin(ry * Math.PI / 180);
  const y = (geo && geo.yG) ? (geo.yG - 1) : 210;

  const bSv = bPt.vx * cr + bPt.vz * sr;
  const bFlip = bSv >= 0 ? 1 : -1;
  // ย่อขนาดรถมอเตอร์ไซค์ให้เล็กลงสมส่วนกับตึก (0.76 เท่า)
  motoEl.style.transform = \`translate3d(\${bPt.x}px,\${y}px,\${bPt.z}px) rotateY(\${-ry}deg) rotateX(\${-rx}deg) scale(\${bFlip * 0.76},\${0.76})\`;

  const dSv = dPt.vx * cr + dPt.vz * sr;
  const dFlip = dSv >= 0 ? 1 : -1;
  // ปรับขนาดน้องหมาให้ตัวกะทัดรัดเท่าแมวในตึก 1 (0.58 เท่า)
  dogEl.style.transform = \`translate3d(\${dPt.x}px,\${y}px,\${dPt.z}px) rotateY(\${-ry}deg) rotateX(\${-rx}deg) scale(\${dFlip * 0.58},\${0.58})\`;

  requestAnimationFrame(tickBikeDog);
}
requestAnimationFrame(tickBikeDog);
`
        : `
// ตึก 2, 3: ไม่มีสัตว์เลี้ยง
`)
}
</script>
</body>
</html>`;
}

const REACT_ROOM_THEMES = [
  {
    themeName: 'มุมทำงาน & อ่านหนังสือ',
    icon: '💻',
    act1Name: 'อ่านหนังสือ & ทำงาน 💻',
    act1Icon: '💻',
    act2Name: 'เล่นกีตาร์โปร่ง 🎸',
    act2Icon: '🎸',
    rugGrad: 'radial-gradient(circle, #38bdf8 0%, #0284c7 100%)',
    wallColor: 'linear-gradient(180deg, #e0f2fe, #bae6fd)',
    decor1: { emoji: '🪴', name: 'ต้นมอนสเตอร่า', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🎸', name: 'กีตาร์โปร่ง', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '💻', name: 'โต๊ะทำงาน & โน้ตบุ๊ก', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '📚 ตู้หนังสือ',
    tvScreen: 'SMART TV'
  },
  {
    themeName: 'สายเกมมิ่ง & อีสปอร์ต',
    icon: '🎮',
    act1Name: 'เล่นเกมคอนโซล 🎮',
    act1Icon: '🎮',
    act2Name: 'สวมหูฟัง RGB 🎧',
    act2Icon: '🎧',
    rugGrad: 'repeating-linear-gradient(45deg, #a855f7 0 5px, #7c3aed 5px 10px)',
    wallColor: 'linear-gradient(180deg, #f3e8ff, #e9d5ff)',
    decor1: { emoji: '🧸', name: 'ตุ๊กตาหมี', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🎧', name: 'หูฟังเกมมิ่ง', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🎮', name: 'จอยเกม & คอนโซล', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🕹️ RGB SETUP',
    tvScreen: 'GAME ON'
  },
  {
    themeName: 'คาเฟ่อบอุ่น & จิบกาแฟ',
    icon: '☕',
    act1Name: 'จิบกาแฟพักผ่อน ☕',
    act1Icon: '☕',
    act2Name: 'อ่านหนังสือใต้โคมไฟ 💡',
    act2Icon: '💡',
    rugGrad: 'radial-gradient(circle, #fde68a 0%, #d97706 100%)',
    wallColor: 'linear-gradient(180deg, #fefce8, #fef3c7)',
    decor1: { emoji: '🌿', name: 'ต้นยางอินเดีย', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '💡', name: 'โคมไฟตั้งพื้น', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '☕', name: 'โต๊ะกาแฟ & โซฟา', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '☕ COZY CAFE',
    tvScreen: 'CHILL L-FI'
  },
  {
    themeName: 'รักสุขภาพ & ฟิตเนส',
    icon: '🧘',
    act1Name: 'เล่นโยคะฟิตเนส 🧘',
    act1Icon: '🧘',
    act2Name: 'ยกดัมเบลออกกำลัง 🏋️',
    act2Icon: '🏋️',
    rugGrad: 'radial-gradient(circle, #a7f3d0 0%, #059669 100%)',
    wallColor: 'linear-gradient(180deg, #ecfdf5, #d1fae5)',
    decor1: { emoji: '🌵', name: 'แคคตัส', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🏋️', name: 'ดัมเบลคู่', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🧘', name: 'เสื่อโยคะ', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🥗 HEALTHY',
    tvScreen: 'YOGA CLASS'
  },
  {
    themeName: 'ดนตรี & ศิลปะ',
    icon: '🎨',
    act1Name: 'เล่นเปียโนไฟฟ้า 🎹',
    act1Icon: '🎹',
    act2Name: 'วาดรูปบนขาตั้ง 🎨',
    act2Icon: '🎨',
    rugGrad: 'repeating-linear-gradient(135deg, #f472b6 0 6px, #ec4899 6px 12px)',
    wallColor: 'linear-gradient(180deg, #fdf2f8, #fce7f3)',
    decor1: { emoji: '🎹', name: 'เปียโนไฟฟ้า', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🎨', name: 'ขาตั้งภาพวาด', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🪑', name: 'เก้าอี้พักผ่อน', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🎼 STUDIO',
    tvScreen: 'MUSIC TV'
  },
  {
    themeName: 'ธรรมชาติ & ชงชาญี่ปุ่น',
    icon: '🍵',
    act1Name: 'ชงชาญี่ปุ่น 🍵',
    act1Icon: '🍵',
    act2Name: 'จัดแจกันไผ่กวนอิม 🎋',
    act2Icon: '🎋',
    rugGrad: 'repeating-linear-gradient(90deg, #d4d4d8 0 4px, #a1a1aa 4px 8px)',
    wallColor: 'linear-gradient(180deg, #f4f4f5, #e4e4e7)',
    decor1: { emoji: '🎋', name: 'ไผ่กวนอิม', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🪞', name: 'กระจกแต่งตัว', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🍵', name: 'โต๊ะชงชาญี่ปุ่น', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🎍 ZEN GARDEN',
    tvScreen: 'NATURE 4K'
  },
  {
    themeName: 'มินิมอล & พักผ่อนสบาย',
    icon: '🛋️',
    act1Name: 'ฟังวิทยุเรโทร 📻',
    act1Icon: '📻',
    act2Name: 'พักผ่อนบนโซฟานุ่ม 🛋️',
    act2Icon: '🛋️',
    rugGrad: 'radial-gradient(circle, #fed7aa 0%, #ea580c 100%)',
    wallColor: 'linear-gradient(180deg, #fff7ed, #ffedd5)',
    decor1: { emoji: '📻', name: 'วิทยุเรโทร', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🪴', name: 'กระถางแคคตัส', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🛋️', name: 'โซฟามินิมอล', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '🛋️ LIVING',
    tvScreen: 'MOVIE HD'
  },
  {
    themeName: 'สายไอที & แกดเจ็ต',
    icon: '🖥️',
    act1Name: 'เขียนโค้ดคอมพิวเตอร์ 💻',
    act1Icon: '💻',
    act2Name: 'คุมเครื่องพิมพ์ 3D 🖨️',
    act2Icon: '🖨️',
    rugGrad: 'repeating-linear-gradient(45deg, #60a5fa 0 6px, #2563eb 6px 12px)',
    wallColor: 'linear-gradient(180deg, #eff6ff, #dbeafe)',
    decor1: { emoji: '🖨️', name: 'เครื่องพิมพ์ 3D', pos: 'top:16px;left:8px;' },
    decor2: { emoji: '🤖', name: 'หุ่นยนต์ฟิกเกอร์', pos: 'top:16px;right:8px;' },
    centerDecor: { emoji: '🖥️', name: 'สถานีคอมพิวเตอร์', pos: 'bottom:80px;left:50%;transform:translateX(-50%);' },
    backDecor: '⚡ TECH LAB',
    tvScreen: 'CODING...'
  }
];

function getReactRoomTheme(roomNo) {
  const seed = parseInt(String(roomNo || '101').replace(/[^0-9]/g, ''), 10) || 101;
  const idx = (seed * 5 + 3) % REACT_ROOM_THEMES.length;
  return REACT_ROOM_THEMES[idx];
}

export default function BuildingInteractive3D({
  buildingId = 1,
  buildingName = "อาคาร 1",
  theme = "pink",
  hasCats = (buildingId === 1),
  rooms = [],
  staffCandidates = [],
  isTraineeMode = false,
  onSelectRoomCheckIn,
  onSelectRoomCheckOut,
  onSelectEditStay,
  onSelectEditRoom,
  onQuickIncreaseBedCapacity,
  onSwitchToElectricityTab
}) {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [internalStaff, setInternalStaff] = useState([]);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!staffCandidates || staffCandidates.length === 0) {
      Promise.allSettled([
        axios("/api/v1/dormitory/staff-candidates").then((r) => r.data),
        axios("/api/v1/userview?isShowAll=true").then((r) => r.data)
      ])
        .then(([scRes, uvRes]) => {
          let sList = (scRes.status === "fulfilled" && scRes.value && (scRes.value.data || scRes.value)) || [];
          if (!Array.isArray(sList) && sList.data) sList = sList.data;
          const uList = (uvRes.status === "fulfilled" && uvRes.value && (uvRes.value.data || uvRes.value)) || [];
          const users = Array.isArray(uList) ? uList : (uList.data || []);
          if (Array.isArray(sList) && sList.length > 0) {
            const enriched = sList.map((s) => {
              const matched = users.find((u) => {
                if (s.citizenId && u.cid && String(s.citizenId) === String(u.cid)) return true;
                if (s.empCode && u.userID && String(s.empCode) === String(u.userID)) return true;
                const sName = (s.name || "").replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, "").trim();
                const uName = `${u.firstName || ""} ${u.lastName || ""}`.trim();
                return sName && uName && (sName === uName || sName.includes(uName) || uName.includes(sName));
              });
              const target = matched?.userName || s.userName || s.empCode || s.id;
              return {
                ...s,
                userName: matched?.userName || s.userName,
                avatarUrl: target ? `https://e.trangskin.go.th/avatar/${target}.jpg` : null
              };
            });
            setInternalStaff(enriched);
          }
        })
        .catch(() => {});
    }
  }, [staffCandidates]);

  const effectiveStaff = (staffCandidates && staffCandidates.length > 0) ? staffCandidates : internalStaff;

  const isSlateTheme = theme === "gray" || theme === "slate" || buildingId === 4;
  const isCreamTheme = !isSlateTheme && (theme === "cream" || theme === "orange" || buildingId === 3);
  const isGreenTheme = !isSlateTheme && !isCreamTheme && (theme === "green" || buildingId === 2);
  const allowCats = Boolean(hasCats) && buildingId === 1;

  // Generate HTML based on live database rooms and building config
  const srcDoc = useMemo(() => {
    return generateBuildingHTML({
      buildingId,
      buildingName,
      theme: isSlateTheme ? "slate" : (isCreamTheme ? "cream" : (isGreenTheme ? "green" : "pink")),
      hasCats: allowCats,
      roomsData: rooms,
      staffCandidates: effectiveStaff,
      isTraineeMode: isTraineeMode
    });
  }, [buildingId, buildingName, isSlateTheme, isCreamTheme, isGreenTheme, allowCats, rooms, effectiveStaff, isTraineeMode]);

  // Listen for room clicks from inside the 3D model iframe
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data && e.data.type === "DORM_3D_ROOM_CLICK") {
        const roomNo = String(e.data.roomNo);
        const targetBuildingId = Number(e.data.buildingId || buildingId);
        const targetRoom = rooms.find(
          (r) =>
            String(r.RoomNumber || r.roomNumber || r.roomNo) === roomNo &&
            (
              (r.DormitoryID !== undefined && Number(String(r.DormitoryID).replace(/\D+/g, "")) === targetBuildingId) ||
              (r.dormitoryID !== undefined && Number(String(r.dormitoryID).replace(/\D+/g, "")) === targetBuildingId) ||
              (r.BuildingNo !== undefined && String(r.BuildingNo).replace(/\D+/g, "") === String(targetBuildingId)) ||
              (r.buildingNo !== undefined && String(r.buildingNo).replace(/\D+/g, "") === String(targetBuildingId)) ||
              (r.buildingName && String(r.buildingName).includes(String(targetBuildingId))) ||
              (r.BuildingName && String(r.BuildingName).includes(String(targetBuildingId))) ||
              (roomNo.length >= 3 && /^[1-4]/.test(roomNo) && Number(roomNo.charAt(0)) === targetBuildingId)
            )
        );
        if (targetRoom) {
          setSelectedRoom(targetRoom);
        } else {
          // Fallback if not found
          setSelectedRoom({
            RoomNumber: roomNo,
            Floor: e.data.floor || (roomNo.length >= 2 ? parseInt(roomNo.substring(1, 2)) : 1),
            BedCapacity: 2,
            CurrentOccupancy: 0,
            Status: "AVAILABLE",
            CurrentResidents: [],
            DormitoryID: targetBuildingId,
            BuildingNo: String(targetBuildingId)
          });
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [rooms, buildingId]);

  // Parse all occupants including family members and co-residents
  const rawResidents = selectedRoom?.CurrentResidents || selectedRoom?.currentResidents || [];
  const occupantsList = [];
  rawResidents.forEach((res) => {
    const mName = res.TraineeName || res.traineeName;
    if (mName) {
      const cleanName = mName.replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
      const foundStaff = (effectiveStaff || []).find((s) => {
        if (res.StaffCode && s.empCode && String(res.StaffCode) === String(s.empCode)) return true;
        if (res.EmployeeKey && s.id && String(res.EmployeeKey) === String(s.id)) return true;
        if (res.CitizenID && s.citizenId && String(res.CitizenID) === String(s.citizenId)) return true;
        const sName = (s.name || '').replace(/^(นพ\.|พญ\.|นาย|นางสาว|นาง|น\.ส\.|ดร\.)\s*/, '').trim();
        return cleanName && sName && (cleanName === sName || sName.includes(cleanName) || cleanName.includes(sName));
      });

      let avatarUrl = res.avatarUrl || res.AvatarUrl || null;
      let isStaffLinked = false;
      if (foundStaff) {
        isStaffLinked = true;
        const target = foundStaff.userName || foundStaff.empCode || foundStaff.id;
        avatarUrl = foundStaff.avatarUrl || (target ? `https://e.trangskin.go.th/avatar/${target}.jpg` : avatarUrl);
      } else if (res.StaffCode || res.EmployeeKey) {
        isStaffLinked = true;
        const target = res.StaffCode || res.EmployeeKey;
        if (!avatarUrl && target) avatarUrl = `https://e.trangskin.go.th/avatar/${target}.jpg`;
      } else if (res.TraineeType === "STAFF" || res.traineeType === "STAFF") {
        isStaffLinked = true;
      }

      const gRaw = /^(นางสาว|นาง|พญ\.|น\.ส\.)/.test(mName) ? 'F' : (/^(นาย|นพ\.)/.test(mName) ? 'M' : (buildingId === 1 ? 'F' : (buildingId === 4 ? 'M' : 'F')));
      occupantsList.push({
        name: mName,
        gender: gRaw,
        role: res.PositionText || res.positionText || (foundStaff ? foundStaff.position : null) || (
          buildingId === 2 ? "แพทย์" :
          (buildingId === 3 ? "บุคลากร (ผู้พักหลัก)" :
          (buildingId === 4 ? (String(selectedRoom?.RoomNumber) === "424" ? "บุคลากร / ครอบครัวพิเศษ" : "เจ้าหน้าที่ / นศ.ชาย") :
          "เจ้าหน้าที่"))
        ),
        isFamily: false,
        isStaffLinked: isStaffLinked,
        avatarUrl: avatarUrl,
        rawStay: res
      });
    }

    const coRaw = res.CoResidents || res.coResidents || "";
    if (coRaw && typeof coRaw === "string") {
      const members = coRaw.split(/[,;\n]+/).map((m) => m.trim()).filter(Boolean);
      members.forEach((m) => {
        const fgRaw = /^(นางสาว|นาง|พญ\.|น\.ส\.|ด\.ญ\.)/.test(m) ? 'F' : (/^(นาย|นพ\.|ด\.ช\.)/.test(m) ? 'M' : 'F');
        occupantsList.push({
          name: m,
          gender: fgRaw,
          role: "สมาชิกครอบครัว / ผู้ติดตาม",
          isFamily: true,
          isStaffLinked: false,
          avatarUrl: null,
          rawStay: res
        });
      });
    }
  });

  const totalOccupants = occupantsList.length;
  const cap = Number(selectedRoom?.BedCapacity || selectedRoom?.bedCapacity || 2);
  const isOverCapacity = totalOccupants > cap;
  const isVacant = totalOccupants === 0;
  const isMaintenance = selectedRoom?.Status === "MAINTENANCE" || selectedRoom?.status === "MAINTENANCE";
  const isRoom424SpecialFamily = (buildingId === 4 && String(selectedRoom?.RoomNumber) === "424");
  const isRoom411Duty = (buildingId === 4 && String(selectedRoom?.RoomNumber) === "411");

  const isRoomOccupiedByStaff = Boolean(
    isTraineeMode && (
      rawResidents.some((r) => 
        r.TraineeType === "STAFF" || 
        r.traineeType === "STAFF" || 
        r.StaffCode || 
        r.EmployeeKey || 
        r.CitizenID ||
        (r.PositionText && r.PositionText.includes("เจ้าหน้าที่")) ||
        (r.positionText && r.positionText.includes("เจ้าหน้าที่"))
      ) || occupantsList.some((occ) => occ.isStaffLinked)
    )
  );

  return (
    <div className="w-100 position-relative" style={{ fontFamily: '"Sarabun", sans-serif' }}>
      {/* 3D Model Iframe */}
      <div
        className="shadow-sm border-0 position-relative overflow-hidden"
        style={{
          borderRadius: "20px",
          backgroundColor: isSlateTheme ? "#f1f5f9" : (isCreamTheme ? "#fefce8" : (isGreenTheme ? "#ecfdf5" : "#bae6fd")),
          border: isSlateTheme ? "2.5px solid #475569" : (isCreamTheme ? "2.5px solid #d97706" : (isGreenTheme ? "2px solid #10b981" : "2px solid #f472b6"))
        }}
      >
        <iframe
          ref={iframeRef}
          title={`3D Building ${buildingId}`}
          srcDoc={srcDoc}
          style={{
            width: "100%",
            height: "720px",
            border: "none",
            display: "block"
          }}
        />
      </div>

      {/* Room Management Modal triggered on click */}
      <Modal
        isOpen={Boolean(selectedRoom)}
        toggle={() => setSelectedRoom(null)}
        centered
        size="md"
        style={{ fontFamily: '"Sarabun", sans-serif' }}
      >
        <ModalHeader
          toggle={() => setSelectedRoom(null)}
          style={{
            backgroundColor: isSlateTheme ? "#334155" : (isCreamTheme ? "#b45309" : (isGreenTheme ? "#059669" : "#be185d")),
            color: "#ffffff",
            fontWeight: "bold",
            borderBottom: "none",
            fontFamily: '"Sarabun", sans-serif'
          }}
        >
          🏢 จัดการห้อง {selectedRoom?.RoomNumber} • {buildingName} ชั้น {selectedRoom?.Floor}
        </ModalHeader>
        <ModalBody style={{ backgroundColor: isSlateTheme ? "#f8fafc" : (isCreamTheme ? "#fffbeb" : (isGreenTheme ? "#f0fdf4" : "#fdf2f8")), padding: "20px" }}>
          {selectedRoom && (
            <div>
              {/* Special Room 424 Badge */}
              {isRoom424SpecialFamily && (
                <div
                  className="p-2 px-3 mb-3 rounded d-flex align-items-center"
                  style={{
                    backgroundColor: "#fffbeb",
                    border: "1.5px solid #f59e0b",
                    color: "#92400e",
                    fontSize: "0.85rem",
                    fontWeight: "700"
                  }}
                >
                  <span style={{ fontSize: "1.3rem", marginRight: "8px" }}>👨‍👩‍👧</span>
                  <div>
                    <div>ห้องครอบครัวพิเศษ (ตามมติกรรมการบ้านพัก)</div>
                    <div style={{ fontSize: "0.76rem", fontWeight: "normal", color: "#b45309" }}>
                      จัดสรรเป็นกรณีพิเศษสำหรับครอบครัวเนื่องจากตึก 3 เต็ม สามารถระบุสมาชิกครอบครัวร่วมพักได้
                    </div>
                  </div>
                </div>
              )}

              {/* Special Room 411 Badge */}
              {isRoom411Duty && (
                <div
                  className="p-2 px-3 mb-3 rounded d-flex align-items-center"
                  style={{
                    backgroundColor: "#f0f9ff",
                    border: "1.5px solid #38bdf8",
                    color: "#0369a1",
                    fontSize: "0.85rem",
                    fontWeight: "700"
                  }}
                >
                  <span style={{ fontSize: "1.3rem", marginRight: "8px" }}>🛏️</span>
                  <div>
                    <div>ห้องพักเวร (ชั้น 1 อาคาร 4)</div>
                    <div style={{ fontSize: "0.76rem", fontWeight: "normal", color: "#0284c7" }}>
                      ห้องจัดสรรสำหรับเจ้าหน้าที่อยู่เวรปฏิบัติการ
                    </div>
                  </div>
                </div>
              )}

              {/* Over-capacity Alert Banner with 1-click bed increase */}
              {isOverCapacity && (
                <div
                  className="p-3 mb-3 rounded shadow-sm"
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "2px solid #ef4444",
                    color: "#991b1b"
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <div style={{ fontWeight: 800, fontSize: "0.96rem" }}>
                        ⚠️ ผู้พักรวม ({totalOccupants} คน) เกินจำนวนเตียงของห้อง ({cap} เตียง)!
                      </div>
                      <div style={{ fontSize: "0.82rem", color: "#b91c1c", marginTop: "2px" }}>
                        ห้องนี้มีสมาชิกเกินจำนวนเตียง กรุณากดเพิ่มจำนวนเตียงเพื่อรองรับทุกคน
                      </div>
                    </div>
                    <Button
                      color="danger"
                      size="sm"
                      style={{ fontWeight: 800, borderRadius: "8px", padding: "6px 12px", whiteSpace: "nowrap" }}
                      onClick={async () => {
                        if (onQuickIncreaseBedCapacity) {
                          await onQuickIncreaseBedCapacity(selectedRoom, totalOccupants);
                          setSelectedRoom((prev) => prev ? { ...prev, BedCapacity: totalOccupants, bedCapacity: totalOccupants } : null);
                        } else if (onSelectEditRoom) {
                          setSelectedRoom(null);
                          onSelectEditRoom({
                            ...selectedRoom,
                            BedCapacity: totalOccupants,
                            suggestedBedCapacity: totalOccupants
                          });
                        }
                      }}
                    >
                      🛏️ เพิ่มจำนวนเตียงเป็น {totalOccupants} เตียงทันที
                    </Button>
                  </div>
                </div>
              )}

              {/* Room Status Overview */}
              <div
                className="d-flex align-items-center justify-content-between p-3 mb-3 bg-white"
                style={{
                  borderRadius: "14px",
                  border: isSlateTheme ? "1.5px solid #cbd5e1" : (isCreamTheme ? "1.5px solid #fde047" : (isGreenTheme ? "1.5px solid #86efac" : "1.5px solid #fbcfe8")),
                  boxShadow: isSlateTheme ? "0 2px 6px rgba(51,65,85,0.08)" : (isCreamTheme ? "0 2px 6px rgba(180,83,9,0.08)" : (isGreenTheme ? "0 2px 6px rgba(5,150,105,0.08)" : "0 2px 6px rgba(190,24,93,0.06)"))
                }}
              >
                <div>
                  <h4 style={{ margin: 0, fontWeight: "800", color: isSlateTheme ? "#1e293b" : (isCreamTheme ? "#78350f" : (isGreenTheme ? "#065f46" : "#831843")) }}>
                    ห้องพัก {selectedRoom.RoomNumber}
                  </h4>
                  <small style={{ color: "#64748b", fontWeight: "600" }}>
                    {buildingName} ชั้น {selectedRoom.Floor}
                  </small>
                </div>
                <div>
                  {isRoomOccupiedByStaff ? (
                    <Badge color="secondary" style={{ fontSize: "0.88rem", padding: "6px 12px", borderRadius: "10px", backgroundColor: "#334155", color: "#fff" }}>
                      🔒 มีเจ้าหน้าที่พักอยู่ (ดูอย่างเดียว)
                    </Badge>
                  ) : isMaintenance ? (
                    <Badge color="secondary" style={{ fontSize: "0.88rem", padding: "6px 12px", borderRadius: "10px", backgroundColor: "#64748b" }}>
                      🔧 ปิดปรับปรุง
                    </Badge>
                  ) : isVacant ? (
                    <Badge color="success" style={{ fontSize: "0.88rem", padding: "6px 12px", borderRadius: "10px", backgroundColor: "#059669" }}>
                      🟢 ว่างพร้อมพัก
                    </Badge>
                  ) : isOverCapacity ? (
                    <Badge color="danger" style={{ fontSize: "0.88rem", padding: "6px 12px", borderRadius: "10px", backgroundColor: "#dc2626" }}>
                      ⚠️ เกินเตียง ({totalOccupants}/{cap})
                    </Badge>
                  ) : totalOccupants < cap ? (
                    <Badge color="warning" style={{ fontSize: "0.88rem", padding: "6px 12px", borderRadius: "10px", backgroundColor: "#f59e0b", color: "#fff" }}>
                      🟡 ว่างอีก {cap - totalOccupants} คน
                    </Badge>
                  ) : (
                    <Badge color="danger" style={{ fontSize: "0.88rem", padding: "6px 12px", borderRadius: "10px", backgroundColor: "#e11d48" }}>
                      🔴 ไม่ว่าง (เต็ม)
                    </Badge>
                  )}
                </div>
              </div>

              {/* Notice for Staff Room in Trainee Mode */}
              {isRoomOccupiedByStaff && (
                <div
                  className="p-3 mb-3 d-flex align-items-center"
                  style={{
                    borderRadius: "14px",
                    backgroundColor: "#f8fafc",
                    border: "1.5px solid #cbd5e1",
                    gap: "10px"
                  }}
                >
                  <span style={{ fontSize: "26px" }}>🔒</span>
                  <div>
                    <div style={{ fontWeight: "800", color: "#1e293b", fontSize: "0.94rem" }}>
                      ห้องนี้มีเจ้าหน้าที่โรงพยาบาลพักอาศัยอยู่
                    </div>
                    <div style={{ fontSize: "0.82rem", color: "#64748b" }}>
                      หน้านี้เป็นระบบสำหรับนักศึกษา จึงไม่สามารถจัดนักศึกษาเข้าพักหรือแก้ไขห้องนี้ได้ (สงวนสิทธิ์การจัดการที่ระบบหอพักบุคลากร)
                    </div>
                  </div>
                </div>
              )}

              {/* Capacity and Rate info */}
              <div
                className="p-3 mb-3 bg-white"
                style={{
                  borderRadius: "14px",
                  border: isSlateTheme ? "1.5px solid #cbd5e1" : (isCreamTheme ? "1.5px solid #fde047" : (isGreenTheme ? "1.5px solid #86efac" : "1.5px solid #fbcfe8"))
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span style={{ fontSize: "0.88rem", color: "#64748b", fontWeight: "700" }}>
                    🛏️ ความจุเตียง: <b>{cap} เตียง</b>
                  </span>
                  <span style={{ fontSize: "0.88rem", color: isOverCapacity ? "#dc2626" : (isSlateTheme ? "#1e293b" : (isCreamTheme ? "#78350f" : (isGreenTheme ? "#065f46" : "#831843"))), fontWeight: "700" }}>
                    เข้าพักแล้ว: <b>{totalOccupants} / {cap} คน</b> {isOverCapacity ? "(⚠️ เกินเตียง)" : ""}
                  </span>
                </div>
                <div className="progress" style={{ height: "8px", borderRadius: "4px" }}>
                  <div
                    className="progress-bar"
                    style={{
                      width: `${Math.min(100, Math.round((totalOccupants / cap) * 100))}%`,
                      backgroundColor: isVacant ? "#059669" : (isOverCapacity ? "#dc2626" : (totalOccupants >= cap ? "#e11d48" : "#f59e0b"))
                    }}
                  />
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3 pt-2" style={{ borderTop: "1px dashed #e2e8f0" }}>
                  <span style={{ fontSize: "0.85rem", color: "#475569" }}>
                    อัตราค่าห้อง: <b>{selectedRoom.fee || 500} บาท/เดือน</b>
                  </span>
                  {selectedRoom.electricityAmount > 0 ? (
                    <span className="badge badge-warning" style={{ backgroundColor: "#fef3c7", color: "#92400e", border: "1px solid #f59e0b", fontSize: "0.78rem", padding: "4px 8px" }}>
                      ⚡ ค่าไฟ +{Number(selectedRoom.electricityAmount).toLocaleString()} บ. (รวม {(Number(selectedRoom.fee || 500) + Number(selectedRoom.electricityAmount)).toLocaleString()} บ.)
                    </span>
                  ) : (
                    <span style={{ fontSize: "0.82rem", color: "#64748b" }}>
                      (ยังไม่มีการจดมิเตอร์ไฟ)
                    </span>
                  )}
                </div>
                {selectedRoom.electricityAmount > 0 && selectedRoom.electricity && (
                  <div className="mt-2 p-2 rounded" style={{ backgroundColor: "#fffbeb", fontSize: "0.78rem", color: "#92400e", border: "1px solid #fde047" }}>
                    ⚡ <b>มิเตอร์ไฟห้องนี้:</b> {selectedRoom.electricity.previousMeter} ➔ {selectedRoom.electricity.currentMeter} ({selectedRoom.electricity.netUnits || selectedRoom.electricity.totalUnits} หน่วย @ {selectedRoom.electricity.ratePerUnit || 8} บ.) = {Number(selectedRoom.electricityAmount).toLocaleString()} บ.
                    {totalOccupants > 1 ? ` (เฉลี่ยคนละ ${Math.round(selectedRoom.electricityAmount / totalOccupants).toLocaleString()} บ.)` : ""}
                  </div>
                )}
              </div>

              {/* Residents & Family Roster */}
              <div
                className="p-3 mb-3 bg-white"
                style={{
                  borderRadius: "14px",
                  border: isSlateTheme ? "1.5px solid #cbd5e1" : (isCreamTheme ? "1.5px solid #fde047" : (isGreenTheme ? "1.5px solid #86efac" : "1.5px solid #fbcfe8"))
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span style={{ fontSize: "0.85rem", fontWeight: "800", color: isSlateTheme ? "#1e293b" : (isCreamTheme ? "#78350f" : (isGreenTheme ? "#065f46" : "#831843")) }}>
                    👥 ผู้พักอาศัย{isRoom424SpecialFamily ? "และสมาชิกครอบครัว" : ""} ({totalOccupants} คน)
                  </span>
                  {isOverCapacity && (
                    <Badge color="danger" style={{ fontSize: "0.75rem", borderRadius: "6px" }}>
                      ⚠️ เกินเตียง (+{totalOccupants - cap} คน)
                    </Badge>
                  )}
                </div>

                {occupantsList.length === 0 ? (
                  <div
                    className="text-center py-3 text-muted"
                    style={{
                      backgroundColor: "#f8fafc",
                      borderRadius: "10px",
                      border: "1px dashed #cbd5e1",
                      fontSize: "0.88rem"
                    }}
                  >
                    ✓ ยังไม่มีผู้เข้าพักในห้องนี้ (ห้องว่าง)
                  </div>
                ) : (
                  occupantsList.map((occItem, idx) => (
                    <div
                      key={idx}
                      className="d-flex align-items-center justify-content-between p-2 mb-2"
                      style={{
                        backgroundColor: occItem.isFamily ? (isCreamTheme ? "#fefce8" : "#f8fafc") : (isSlateTheme ? "#f1f5f9" : (isCreamTheme ? "#fffbeb" : (isGreenTheme ? "#f0fdf4" : "#fdf2f8"))),
                        borderRadius: "10px",
                        border: occItem.isFamily ? "1px dashed #f59e0b" : (isSlateTheme ? "1px solid #cbd5e1" : (isCreamTheme ? "1px solid #fde047" : (isGreenTheme ? "1px solid #bbf7d0" : "1px solid #fbcfe8")))
                      }}
                    >
                      <div className="d-flex align-items-center">
                        {occItem.avatarUrl && !occItem.isFamily ? (
                          <img
                            src={occItem.avatarUrl}
                            alt={occItem.name}
                            style={{
                              width: "42px",
                              height: "42px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              objectPosition: "center top",
                              marginRight: "10px",
                              border: isRoomOccupiedByStaff ? "2px solid #475569" : "2px solid #0284c7",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                              flexShrink: 0
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              if (e.target.nextElementSibling) {
                                e.target.nextElementSibling.style.display = "flex";
                              }
                            }}
                          />
                        ) : null}
                        <div
                          style={{
                            width: "42px",
                            height: "42px",
                            borderRadius: "50%",
                            backgroundColor: occItem.isFamily ? "#fef3c7" : (isRoomOccupiedByStaff ? "#e2e8f0" : "#e0f2fe"),
                            display: (occItem.avatarUrl && !occItem.isFamily) ? "none" : "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.3rem",
                            marginRight: "10px",
                            border: occItem.isFamily ? "1.5px solid #fde047" : (isRoomOccupiedByStaff ? "1.5px solid #94a3b8" : "1.5px solid #bae6fd"),
                            flexShrink: 0
                          }}
                        >
                          {occItem.isFamily ? "👨‍👩‍👧" : (isRoomOccupiedByStaff ? "🩺" : "👤")}
                        </div>
                        <div>
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <strong style={{ color: "#0f172a", fontSize: "0.92rem" }}>
                              {occItem.name}
                            </strong>
                            {occItem.isFamily ? (
                              <Badge color="warning" className="ml-1 text-dark" style={{ fontSize: "0.7rem", backgroundColor: "#fef08a", border: "1px solid #facc15" }}>
                                สมาชิกครอบครัว
                              </Badge>
                            ) : (
                              <Badge color="light" className="ml-1" style={{ fontSize: "0.7rem", border: "1px solid #cbd5e1" }}>
                                {isRoom424SpecialFamily ? "ผู้พักหลัก (ครอบครัว)" : (isRoomOccupiedByStaff || occItem.isStaffLinked ? "เจ้าหน้าที่" : "ผู้พัก")}
                              </Badge>
                            )}
                          </div>
                          <div style={{ fontSize: "0.78rem", color: "#64748b" }}>
                            {occItem.isFamily
                              ? `ผู้ติดตามของ ${occItem.rawStay?.TraineeName || occItem.rawStay?.traineeName || "ผู้พักหลัก"}`
                              : occItem.role}
                          </div>
                          {occItem.rawStay?.electricityShare > 0 && (
                            <div style={{ fontSize: "0.74rem", color: "#b45309", fontWeight: "600", marginTop: "2px" }}>
                              ⚡ ค่าไฟที่รวม: {Number(occItem.rawStay.electricityShare).toLocaleString()} บ. (ยอดรวมห้อง {Number(occItem.rawStay.roomElectricityTotal).toLocaleString()} บ.)
                            </div>
                          )}
                        </div>
                      </div>
                      {!occItem.isFamily && !isRoomOccupiedByStaff && (
                        <div className="d-flex align-items-center" style={{ gap: "6px" }}>
                          <Button
                            color="primary"
                            size="sm"
                            outline
                            style={{ borderRadius: "8px", fontSize: "0.78rem", fontWeight: "bold" }}
                            title="แก้ไขข้อมูลการเข้าพัก"
                            onClick={() => {
                              setSelectedRoom(null);
                              if (onSelectEditStay) onSelectEditStay(occItem.rawStay, selectedRoom);
                            }}
                          >
                            <EditIcon style={{ fontSize: "15px", marginRight: "3px" }} />
                            แก้ไข
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            style={{ borderRadius: "8px", fontSize: "0.78rem", fontWeight: "bold" }}
                            title="แจ้งย้ายออกจากห้องพัก"
                            onClick={() => {
                              setSelectedRoom(null);
                              if (onSelectRoomCheckOut) onSelectRoomCheckOut(occItem.rawStay, selectedRoom);
                            }}
                          >
                            <ExitToAppIcon style={{ fontSize: "16px", marginRight: "3px" }} />
                            ย้ายออก
                          </Button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Management Action Buttons */}
              {isRoomOccupiedByStaff ? (
                <div
                  className="p-3 mb-2"
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1.5px solid #cbd5e1",
                    borderRadius: "14px",
                    textAlign: "center"
                  }}
                >
                  <div style={{ fontSize: "1.3rem", marginBottom: "4px" }}>🔒</div>
                  <div style={{ fontWeight: "800", color: "#1e293b", fontSize: "0.92rem" }}>
                    ห้องนี้มีเจ้าหน้าที่โรงพยาบาลพักอาศัยอยู่
                  </div>
                  <div style={{ fontSize: "0.82rem", color: "#64748b", marginTop: "4px" }}>
                    หน้านี้สำหรับนักศึกษา จึงไม่สามารถจัดนักศึกษาเข้าพักหรือแก้ไขห้องนี้ได้ (สงวนสิทธิ์การจัดการที่ระบบหอพักบุคลากร)
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {totalOccupants < cap && !isMaintenance && (
                    <Button
                      color="success"
                      block
                      style={{
                        borderRadius: "12px",
                        fontWeight: "800",
                        padding: "10px",
                        fontSize: "0.95rem",
                        backgroundColor: isTraineeMode ? "#0d9488" : (isSlateTheme ? "#334155" : (isCreamTheme ? "#d97706" : "#059669")),
                        border: "none",
                        boxShadow: isTraineeMode ? "0 4px 12px rgba(13,148,136,0.25)" : (isSlateTheme ? "0 4px 12px rgba(51,65,85,0.25)" : (isCreamTheme ? "0 4px 12px rgba(217,119,6,0.25)" : "0 4px 12px rgba(5,150,105,0.2)"))
                      }}
                      onClick={() => {
                        setSelectedRoom(null);
                        if (onSelectRoomCheckIn) onSelectRoomCheckIn(selectedRoom);
                      }}
                    >
                      <PersonAddIcon style={{ fontSize: "20px", marginRight: "6px" }} />
                      {isTraineeMode ? "➕ จัดนักศึกษาเข้าพักห้องนี้ (เปลี่ยนเป็นห้องนักศึกษา)" : "➕ ลงทะเบียนจัดคนเข้าห้องนี้ (Check-in)"}
                    </Button>
                  )}

                  {!isTraineeMode && (
                    <div className="d-flex gap-2 mt-2">
                      <Button
                        color="info"
                        className="flex-fill"
                        style={{
                          borderRadius: "10px",
                          fontWeight: "700",
                          padding: "8px",
                          fontSize: "0.88rem"
                        }}
                        onClick={() => {
                          setSelectedRoom(null);
                          if (onSelectEditRoom) onSelectEditRoom(selectedRoom);
                        }}
                      >
                        <EditIcon style={{ fontSize: "17px", marginRight: "4px" }} />
                        ⚙️ แก้ไขข้อมูลห้อง
                      </Button>

                      <Button
                        color="warning"
                        className="flex-fill"
                        style={{
                          borderRadius: "10px",
                          fontWeight: "700",
                          padding: "8px",
                          fontSize: "0.88rem",
                          color: "#fff"
                        }}
                        onClick={() => {
                          setSelectedRoom(null);
                          if (onSwitchToElectricityTab) onSwitchToElectricityTab(selectedRoom);
                        }}
                      >
                        <FlashOnIcon style={{ fontSize: "17px", marginRight: "4px" }} />
                        ⚡ มิเตอร์ค่าไฟ
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter style={{ backgroundColor: isSlateTheme ? "#f8fafc" : (isCreamTheme ? "#fffbeb" : (isGreenTheme ? "#f0fdf4" : "#fdf2f8")), borderTop: "none" }}>
          <Button
            color="secondary"
            size="sm"
            style={{ borderRadius: "8px" }}
            onClick={() => setSelectedRoom(null)}
          >
            ปิดหน้าต่าง
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
